import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Navigate, Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Shield, Percent, Ticket, Megaphone, Send, Plus, Trash2, Users2, Car, DollarSign, TrendingUp, Coins, Award, Rocket, Landmark, Copy, Check, Briefcase, Volume2, ShieldAlert, CarFront, QrCode, Eye, X, Sparkles, Pencil, Pause, Play, Target, Gift, Flag, User, Search, FileText, ClipboardList, Wallet, Banknote, Radio, UserCheck, Building2, Activity, MessagesSquare, RefreshCw, AlertTriangle, FlaskConical, Smartphone } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ReactionsAnalyticsCard from "@/components/admin/ReactionsAnalyticsCard";
import AdminQAWalletTab from "@/components/admin/AdminQAWalletTab";
import AdminBrandingTab from "@/components/admin/AdminBrandingTab";
import AdminPwaStats from "@/components/admin/AdminPwaStats";
import AdminMetaPixelCard from "@/components/admin/AdminMetaPixelCard";
import AdminUserEditPanel from "@/components/admin/AdminUserEditPanel";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminLandingContent from "@/components/AdminLandingContent";
import AdminInviteSettings from "@/components/AdminInviteSettings";
import AdminGeoStats from "@/components/AdminGeoStats";
import AdminDisputesTab from "@/components/AdminDisputesTab";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import ImageUploader from "@/components/ImageUploader";
import AdminPaymentMethodsCard from "@/components/admin/AdminPaymentMethodsCard";
import AdminWithdrawGateCard from "@/components/admin/AdminWithdrawGateCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className="text-xl font-bold tracking-tight">{value}</div>
    </Card>
  );
}

// ---------- Settings tab ----------
function SettingsTab() {
  const [commission, setCommission] = useState(0);
  const [withdrawal, setWithdrawal] = useState(5);
  const [userMin, setUserMin] = useState(10);
  const [userMax, setUserMax] = useState(5000);
  const [userDaily, setUserDaily] = useState(2000);
  const [lockupDays, setLockupDays] = useState(90);
  const [wdMode, setWdMode] = useState("manual");
  const [pendingWds, setPendingWds] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadWds = async () => {
    try {
      const r = await axios.get(`${API}/admin/withdrawals?status=pending`);
      setPendingWds(r.data);
    } catch (err) {
      console.error("[Admin] loadWds failed", err);
    }
  };

  useEffect(() => {
    axios.get(`${API}/admin/settings`).then((r) => {
      setCommission(r.data.platform_commission_pct || 0);
      setWithdrawal(r.data.withdrawal_fee_pct ?? 5);
      setUserMin(r.data.user_withdrawal_min ?? 10);
      setUserMax(r.data.user_withdrawal_max ?? 5000);
      setUserDaily(r.data.user_withdrawal_daily_limit ?? 2000);
      setLockupDays(r.data.investor_lockup_days ?? 90);
      setWdMode(r.data.withdrawal_mode || "manual");
    });
    loadWds();
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        platform_commission_pct: commission,
        withdrawal_fee_pct: withdrawal,
        user_withdrawal_min: parseFloat(userMin) || 0,
        user_withdrawal_max: parseFloat(userMax) || 0,
        user_withdrawal_daily_limit: parseFloat(userDaily) || 0,
        investor_lockup_days: parseInt(lockupDays) || 0,
        withdrawal_mode: wdMode,
      });
      toast.success("Taxas atualizadas!");
    } catch { toast.error("Erro ao salvar"); }
    setLoading(false);
  };

  const updateWd = async (id, status) => {
    if (status === "paid" && !window.confirm("Confirma que você já realizou o Pix/transferência para o usuário? O saque será marcado como CONCLUÍDO.")) return;
    if (status === "denied" && !window.confirm("Negar este saque? O valor volta para a carteira do usuário.")) return;
    try {
      await axios.put(`${API}/admin/withdrawals/${id}/status`, { status });
      toast.success(status === "paid" ? "Saque concluído — usuário notificado" : `Saque ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    loadWds();
  };
  const copyDest = (w) => { navigator.clipboard?.writeText(w.destination || ""); toast.success("Chave copiada"); };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Percent className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">Comissão por corrida</h2>
              <p className="text-xs text-muted-foreground">% retida em cada corrida</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm">Comissão</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="50"
                  data-testid="admin-commission-input"
                  value={commission}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") { setCommission(0); return; }
                    const v = parseFloat(raw);
                    if (!isNaN(v)) setCommission(Math.max(0, Math.min(50, v)));
                  }}
                  className="w-20 text-right font-semibold"
                />
                <div className="text-3xl font-bold text-primary min-w-[85px] text-right" data-testid="admin-commission-value">
                  {commission.toFixed(1)}%
                </div>
              </div>
            </div>
            <div className="py-3">
              <Slider data-testid="admin-commission-slider" value={[commission]} onValueChange={(v) => setCommission(v[0])} min={0} max={50} step={0.5} className="cursor-pointer" />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-2 px-0.5">
                <span>0%</span><span>12.5%</span><span>25%</span><span>37.5%</span><span>50%</span>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-green-500/40">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
              <Percent className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">Taxa de saque</h2>
              <p className="text-xs text-muted-foreground">% cobrada quando usuário retira o saldo</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm">Taxa</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="20"
                  data-testid="admin-withdrawal-input"
                  value={withdrawal}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") { setWithdrawal(0); return; }
                    const v = parseFloat(raw);
                    if (!isNaN(v)) setWithdrawal(Math.max(0, Math.min(20, v)));
                  }}
                  className="w-20 text-right font-semibold"
                />
                <div className="text-3xl font-bold text-green-600 min-w-[85px] text-right" data-testid="admin-withdrawal-value">
                  {withdrawal.toFixed(1)}%
                </div>
              </div>
            </div>
            <div className="py-3">
              <Slider data-testid="admin-withdrawal-slider" value={[withdrawal]} onValueChange={(v) => setWithdrawal(v[0])} min={0} max={20} step={0.5} className="cursor-pointer" />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-2 px-0.5">
                <span>0%</span><span>5%</span><span>10%</span><span>15%</span><span>20%</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="text-lg font-bold tracking-tight mb-4">Limites de saque de usuários</h2>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Mínimo por saque (R$)</Label>
            <Input data-testid="admin-user-min" type="number" min="0" value={userMin} onChange={(e) => setUserMin(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Máximo por saque (R$)</Label>
            <Input data-testid="admin-user-max" type="number" min="0" value={userMax} onChange={(e) => setUserMax(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Limite diário (R$)</Label>
            <Input data-testid="admin-user-daily" type="number" min="0" value={userDaily} onChange={(e) => setUserDaily(e.target.value)} />
          </div>
        </div>
      </Card>

      <Card className="p-6 border-primary/30">
        <h2 className="text-lg font-bold tracking-tight mb-4">Prazo mínimo para saque de investidores</h2>
        <div className="grid md:grid-cols-[200px_1fr] gap-3 items-center">
          <div>
            <Label className="text-xs">Dias de carência</Label>
            <Input data-testid="admin-lockup-days" type="number" min="0" value={lockupDays} onChange={(e) => setLockupDays(e.target.value)} />
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
            Investidores não podem sacar aportes durante <strong>{lockupDays} dias</strong> após a compra do plano. Após esse prazo, podem resgatar principal + rendimento acumulado.
          </div>
        </div>
      </Card>

      <Card className="p-6 border-cyan-500/40">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-cyan-600 flex items-center justify-center">
            <Rocket className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold tracking-tight">Modo de processamento de saques</h2>
            <p className="text-xs text-muted-foreground">Define se saques são liberados na hora ou passam por aprovação manual do admin</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <button
            data-testid="admin-wdmode-auto"
            type="button"
            onClick={() => setWdMode("auto")}
            className={`text-left p-4 rounded-xl border-2 transition ${wdMode === "auto" ? "border-cyan-500 bg-cyan-500/10" : "border-border hover:border-cyan-500/40"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-3 h-3 rounded-full ${wdMode === "auto" ? "bg-cyan-500" : "bg-muted"}`} />
              <span className="font-bold">Automático</span>
              <Badge className="bg-green-600 text-[10px]">instantâneo</Badge>
            </div>
            <p className="text-xs text-muted-foreground">Saques são marcados como pagos assim que o usuário confirma o código do e-mail.</p>
          </button>
          <button
            data-testid="admin-wdmode-manual"
            type="button"
            onClick={() => setWdMode("manual")}
            className={`text-left p-4 rounded-xl border-2 transition ${wdMode === "manual" ? "border-cyan-500 bg-cyan-500/10" : "border-border hover:border-cyan-500/40"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-3 h-3 rounded-full ${wdMode === "manual" ? "bg-cyan-500" : "bg-muted"}`} />
              <span className="font-bold">Aprovação manual</span>
              <Badge className="bg-orange-500 text-[10px]">seguro</Badge>
            </div>
            <p className="text-xs text-muted-foreground">Saques ficam pendentes na lista abaixo até que um admin aprove ou negue.</p>
          </button>
        </div>
      </Card>

      <Button data-testid="admin-commission-save" onClick={save} disabled={loading} className="rounded-full">
        {loading ? "Salvando..." : "Salvar configurações"}
      </Button>

      {/* Pending withdrawals */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold tracking-tight">Saques pendentes</h2>
          <Badge>{pendingWds.length}</Badge>
        </div>
        {pendingWds.length === 0 && <p className="text-sm text-muted-foreground">Nenhum saque pendente</p>}
        <div className="space-y-3">
          {pendingWds.map(w => (
            <div key={w.withdrawal_id} className="p-3 rounded-lg border border-border flex items-center justify-between gap-3" data-testid={`admin-wd-${w.withdrawal_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold">R$ {w.amount.toFixed(2)}</span>
                  <Badge variant="secondary">{w.type === "user" ? "Usuário" : "Investidor"}</Badge>
                  <span className="text-xs text-muted-foreground">{w.user_name}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {w.method === "pix" ? "PIX" : "Bancário"}: {w.destination.substring(0, 40)}
                </div>
                {w.type === "investor" && (
                  <div className="text-xs text-muted-foreground">
                    Principal: R$ {(w.principal || 0).toFixed(2)} · Juros: R$ {(w.interest || 0).toFixed(2)}
                  </div>
                )}
              </div>
              <div className="flex gap-1 flex-wrap justify-end">
                <Button size="sm" variant="ghost" onClick={() => copyDest(w)} className="rounded-full text-xs" data-testid={`admin-wd-copy-${w.withdrawal_id}`}>Copiar chave</Button>
                <Button size="sm" onClick={() => updateWd(w.withdrawal_id, "paid")} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid={`admin-wd-paid-${w.withdrawal_id}`}>Pix feito — concluir</Button>
                <Button size="sm" variant="outline" onClick={() => updateWd(w.withdrawal_id, "denied")} className="rounded-full" data-testid={`admin-wd-deny-${w.withdrawal_id}`}>Negar</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ---------- Coins tab ----------
function CoinsTab() {
  const [values, setValues] = useState({});
  const [goals, setGoals] = useState([]);
  const [rate, setRate] = useState(100);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/coins/config`).then((r) => {
      setValues(r.data.coin_values || {});
      setGoals(r.data.coin_goals || []);
      setRate(r.data.coins_per_brl || 100);
    });
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        coin_values: Object.fromEntries(Object.entries(values).map(([k, v]) => [k, parseInt(v) || 0])),
        coin_goals: goals.map(g => ({ ...g, target: parseInt(g.target) || 0 })),
        coins_per_brl: parseFloat(rate) || 100,
      });
      toast.success("Sistema FUTURO PDV atualizado!");
    } catch { toast.error("Erro"); }
    setLoading(false);
  };

  const addGoal = () => setGoals([...goals, { name: "Nova meta", target: 100, reward: "Prêmio" }]);
  const removeGoal = (i) => setGoals(goals.filter((_, idx) => idx !== i));
  const updateGoal = (i, field, val) => setGoals(goals.map((g, idx) => idx === i ? { ...g, [field]: val } : g));

  const actionLabels = {
    post_created: "Criar post",
    comment_created: "Comentar",
    like_given: "Dar curtida",
    job_posted: "Publicar trabalho",
    job_applied: "Candidatar-se",
    ride_completed_rider: "Finalizar corrida (passageiro)",
    ride_completed_driver: "Finalizar corrida (motorista)",
    rating_given: "Avaliar corrida",
    app_shared: "Compartilhar app 📤",
    referral_signup: "Indicação cadastrou-se 🎁",
    referred_signup: "Bônus de quem entra por convite 🎁",
    content_viewed: "Visualizar conteúdo (view)",
    content_shared: "Compartilhar conteúdo",
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <AdminInviteSettings />
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Coin values per action */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-yellow-500 flex items-center justify-center">
              <span className="text-white font-bold text-lg">$</span>
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">Valor por ação</h2>
              <p className="text-xs text-muted-foreground">Quantas unidades FUTURO PDV cada ação vale</p>
            </div>
          </div>
          <div className="space-y-3">
            {Object.keys({ ...actionLabels, ...values }).map((key) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <Label className="text-sm flex-1">{actionLabels[key] || key}</Label>
                <Input
                  data-testid={`coin-action-${key}`}
                  type="number"
                  min="0"
                  value={values[key] ?? 0}
                  onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                  className="w-24 text-right"
                />
              </div>
            ))}
          </div>
        </Card>

        {/* Conversion rate */}
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <span className="text-white font-bold">⇄</span>
              </div>
              <div>
                <h2 className="text-lg font-bold tracking-tight">Conversão para saldo real</h2>
                <p className="text-xs text-muted-foreground">Quantas unidades FUTURO PDV equivalem a R$ 1,00</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <Label className="text-sm">FUTURO PDV por R$ 1,00</Label>
                <div className="text-2xl font-bold tracking-tight text-primary" data-testid="coin-rate-value">{rate}</div>
              </div>
              <Input
                data-testid="coin-rate-input"
                type="number"
                min="1"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
              />
              <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                Exemplo: 500 FUTURO PDV = R$ {(500 / (parseFloat(rate) || 100)).toFixed(2)}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Goals */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
              <span className="text-white font-bold">★</span>
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">Metas dos usuários</h2>
              <p className="text-xs text-muted-foreground">Marcos e recompensas por acúmulo de FUTURO PDV</p>
            </div>
          </div>
          <Button data-testid="coin-add-goal" onClick={addGoal} size="sm" variant="outline" className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Nova meta
          </Button>
        </div>
        <div className="space-y-3">
          {goals.map((g, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_1fr_auto] gap-2 items-center">
              <Input
                data-testid={`goal-name-${i}`}
                placeholder="Nome"
                value={g.name}
                onChange={(e) => updateGoal(i, "name", e.target.value)}
              />
              <Input
                data-testid={`goal-target-${i}`}
                type="number"
                placeholder="FUTURO PDV"
                value={g.target}
                onChange={(e) => updateGoal(i, "target", e.target.value)}
              />
              <Input
                data-testid={`goal-reward-${i}`}
                placeholder="Recompensa"
                value={g.reward}
                onChange={(e) => updateGoal(i, "reward", e.target.value)}
              />
              <Button variant="ghost" size="icon" onClick={() => removeGoal(i)}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          ))}
          {goals.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-6">Nenhuma meta configurada</div>
          )}
        </div>
      </Card>

      <Button data-testid="coins-save" onClick={save} disabled={loading} className="rounded-full">
        {loading ? "Salvando..." : "Salvar sistema FUTURO PDV"}
      </Button>
    </div>
  );
}

// ---------- Coupons tab ----------
function CouponsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", discount_pct: 10, max_uses: 0, active: true });

  const load = async () => {
    const r = await axios.get(`${API}/admin/coupons`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim()) return toast.error("Informe um código");
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: form.code, discount_pct: Number(form.discount_pct),
        max_uses: Number(form.max_uses), active: form.active,
      });
      toast.success("Cupom criado!");
      setForm({ code: "", discount_pct: 10, max_uses: 0, active: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/coupons/${id}`);
    toast.success("Cupom removido");
    load();
  };

  const toggle = async (c) => {
    await axios.put(`${API}/admin/coupons/${c.coupon_id}`, { ...c, active: !c.active });
    load();
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6">
      <Card className="p-5 h-fit">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
            <Ticket className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-bold tracking-tight">Novo cupom</h2>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Código</Label>
            <Input data-testid="admin-coupon-code" placeholder="Ex: BEMVINDO10" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <Label className="text-xs">Desconto (%)</Label>
            <Input data-testid="admin-coupon-discount" type="number" min="0" max="100" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Máx. usos (0 = ilimitado)</Label>
            <Input data-testid="admin-coupon-uses" type="number" min="0" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
          </div>
          <div className="flex items-center justify-between pt-2">
            <Label className="text-sm">Ativo</Label>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
          </div>
          <Button data-testid="admin-coupon-create" onClick={create} className="w-full rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Criar cupom
          </Button>
        </div>
      </Card>

      <div>
        <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">
          Cupons ({items.length})
        </h3>
        <div className="space-y-3">
          {items.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground">Nenhum cupom criado</Card>
          )}
          {items.map((c) => (
            <Card key={c.coupon_id} className="p-4" data-testid={`coupon-${c.coupon_id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="font-mono text-lg font-bold bg-muted px-2 py-0.5 rounded">{c.code}</code>
                    <Badge variant={c.active ? "default" : "secondary"}>{c.active ? "Ativo" : "Inativo"}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    <span className="font-bold text-primary">{c.discount_pct}%</span> de desconto ·{" "}
                    Usos: {c.uses_count || 0}/{c.max_uses || "∞"}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`coupon-toggle-${c.coupon_id}`} />
                  <Button variant="ghost" size="icon" onClick={() => remove(c.coupon_id)} data-testid={`coupon-delete-${c.coupon_id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Promotions tab ----------
function PromotionsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", audience: "all", bonus_amount: 0, active: true });

  const load = async () => {
    const r = await axios.get(`${API}/admin/promotions`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Título e mensagem obrigatórios");
    try {
      await axios.post(`${API}/admin/promotions`, {
        ...form, bonus_amount: Number(form.bonus_amount) || 0,
      });
      toast.success("Promoção criada!");
      setForm({ title: "", body: "", audience: "all", bonus_amount: 0, active: true });
      load();
    } catch { toast.error("Erro"); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/promotions/${id}`);
    toast.success("Removida");
    load();
  };

  const send = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/promotions/${id}/send`);
      toast.success(`Enviada para ${r.data.sent} usuários`);
      load();
    } catch { toast.error("Erro ao enviar"); }
  };

  return (
    <div className="grid lg:grid-cols-[400px_1fr] gap-6">
      <Card className="p-5 h-fit">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-bold tracking-tight">Nova promoção</h2>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Título</Label>
            <Input data-testid="admin-promo-title" placeholder="Ex: Corridas com bônus!" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Mensagem</Label>
            <Textarea data-testid="admin-promo-body" rows={3} placeholder="Detalhes da promoção..." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Público-alvo</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger data-testid="admin-promo-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos usuários</SelectItem>
                <SelectItem value="riders">Somente passageiros</SelectItem>
                <SelectItem value="drivers">Somente motoristas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Valor do bônus (R$)</Label>
            <Input data-testid="admin-promo-bonus" type="number" step="0.01" min="0" placeholder="0 = sem bônus" value={form.bonus_amount} onChange={(e) => setForm({ ...form, bonus_amount: e.target.value })} />
          </div>
          <Button data-testid="admin-promo-create" onClick={create} className="w-full rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Criar promoção
          </Button>
        </div>
      </Card>

      <div>
        <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">
          Promoções ({items.length})
        </h3>
        <div className="space-y-3">
          {items.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground">Nenhuma promoção criada</Card>
          )}
          {items.map((p) => (
            <Card key={p.promo_id} className="p-4 hover-lift" data-testid={`promo-${p.promo_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h4 className="font-bold">{p.title}</h4>
                    <Badge variant="secondary" className="capitalize">{p.audience === "riders" ? "Passageiros" : p.audience === "drivers" ? "Motoristas" : "Todos"}</Badge>
                    {p.bonus_amount > 0 && (
                      <Badge className="bg-green-600">R$ {p.bonus_amount.toFixed(2)}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{p.body}</p>
                  <div className="text-xs text-muted-foreground mt-2">
                    Enviada para {p.sent_count || 0} usuário(s)
                    {p.last_sent_at && ` · último envio: ${new Date(p.last_sent_at).toLocaleString()}`}
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(p.promo_id)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
              <Button data-testid={`promo-send-${p.promo_id}`} onClick={() => send(p.promo_id)} className="rounded-full" size="sm">
                <Send className="w-4 h-4 mr-2" /> Disparar para usuários
              </Button>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Stats tab ----------
function StatsTab() {
  const [stats, setStats] = useState(null);
  const [publicStats, setPublicStats] = useState(null);
  const [resetting, setResetting] = useState(false);

  const loadPublic = () => axios.get(`${API}/stats/public`).then((r) => setPublicStats(r.data)).catch(() => {});
  useEffect(() => {
    const loadAll = () => { axios.get(`${API}/admin/stats`).then((r) => setStats(r.data)).catch(() => {}); loadPublic(); };
    loadAll();
    const iv = setInterval(loadAll, 30000);
    return () => clearInterval(iv);
  }, []);

  const resetCounter = async () => {
    if (!window.confirm("Zerar o contador público de usuários? Os usuários existentes permanecem no banco — apenas o contador da landing volta a 0 e passa a contar novos cadastros.")) return;
    setResetting(true);
    try {
      await axios.post(`${API}/admin/stats/reset-users-counter`);
      toast.success("Contador zerado. Contagem reinicia a partir de agora.");
      loadPublic();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setResetting(false);
  };

  const clearReset = async () => {
    if (!window.confirm("Voltar a contar TODOS os usuários (remove a marca de zerar)?")) return;
    setResetting(true);
    try {
      await axios.post(`${API}/admin/stats/clear-users-counter-reset`);
      toast.success("Contador voltou ao total geral.");
      loadPublic();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setResetting(false);
  };

  const resetRidesCounter = async () => {
    if (!window.confirm("Zerar o contador público de corridas? As corridas existentes permanecem no banco — apenas o contador da landing volta a 0 e passa a somar as próximas corridas concluídas.")) return;
    setResetting(true);
    try {
      await axios.post(`${API}/admin/stats/reset-rides-counter`);
      toast.success("Corridas zeradas. Contagem reinicia a partir de agora.");
      loadPublic();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setResetting(false);
  };

  const clearRidesReset = async () => {
    if (!window.confirm("Voltar a contar TODAS as corridas concluídas?")) return;
    setResetting(true);
    try {
      await axios.post(`${API}/admin/stats/clear-rides-counter-reset`);
      toast.success("Contador de corridas voltou ao total geral.");
      loadPublic();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setResetting(false);
  };

  if (!stats) return <div>Carregando...</div>;
  const epoch = publicStats?.counter_epoch;
  const ridesEpoch = publicStats?.rides_counter_epoch;
  const refresh = () => {
    axios.get(`${API}/admin/stats`).then((r) => setStats(r.data));
    loadPublic();
  };
  const g = stats; // shortcut
  const brl = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-6">
      {/* Overview strip */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Painel · dados reais</div>
          <h2 className="text-xl font-bold tracking-tight">Estatísticas da plataforma</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Números vindos diretamente do banco. Última atualização: <span className="font-mono">{g.generated_at ? new Date(g.generated_at).toLocaleString("pt-BR") : "—"}</span>.
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full gap-1" onClick={refresh} data-testid="admin-stats-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </Button>
      </div>

      {/* KPI grid (real data only) */}
      <section data-testid="stats-users-group">
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Usuários</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Users2}    label="Cadastrados"       value={g.users_group?.total ?? g.users}  color="bg-blue-500/10 text-blue-600" />
          <StatCard icon={UserCheck} label="Novos (24h)"       value={g.users_group?.new_24h ?? 0}     color="bg-cyan-500/10 text-cyan-600" />
          <StatCard icon={Activity}  label="Novos (7 dias)"    value={g.users_group?.new_7d ?? 0}      color="bg-indigo-500/10 text-indigo-600" />
          <StatCard icon={Shield}    label="Administradores"   value={g.users_group?.admins ?? 0}      color="bg-slate-500/10 text-slate-600" />
        </div>
      </section>

      <section data-testid="stats-drivers-group">
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Motoristas</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Car}      label="Cadastrados"       value={g.drivers_group?.registered ?? g.drivers} color="bg-orange-500/10 text-orange-500" />
          <StatCard icon={Radio}    label="Online agora"       value={g.drivers_group?.online_now ?? 0}       color="bg-emerald-500/10 text-emerald-600" />
          <StatCard icon={UserCheck} label="Novos (7 dias)"    value={g.drivers_group?.new_7d ?? 0}           color="bg-amber-500/10 text-amber-600" />
          <StatCard icon={Building2} label="Empresas"           value={g.freelancers_group?.companies ?? 0}   color="bg-fuchsia-500/10 text-fuchsia-600" />
        </div>
      </section>

      <section data-testid="stats-rides-group">
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Corridas & mobilidade</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={TrendingUp} label="Corridas totais"    value={g.rides_group?.total ?? g.rides_total}          color="bg-green-500/10 text-green-600" />
          <StatCard icon={DollarSign} label="Concluídas"          value={g.rides_group?.completed ?? g.rides_completed} color="bg-blue-500/10 text-blue-600" />
          <StatCard icon={CarFront}   label="Em andamento"        value={g.rides_group?.in_progress ?? 0}                color="bg-yellow-500/10 text-yellow-600" />
          <StatCard icon={X}          label="Canceladas"          value={g.rides_group?.canceled ?? 0}                   color="bg-red-500/10 text-red-600" />
        </div>
      </section>

      <AdminPwaStats />
      <AdminMetaPixelCard />

      <section data-testid="stats-jobs-group">
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Trabalho & freelance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Briefcase} label="Vagas cadastradas"   value={g.jobs_group?.total ?? g.jobs}       color="bg-purple-500/10 text-purple-600" />
          <StatCard icon={Play}      label="Vagas abertas"        value={g.jobs_group?.open ?? 0}             color="bg-emerald-500/10 text-emerald-600" />
          <StatCard icon={Check}     label="Concluídas"           value={g.jobs_group?.completed ?? 0}        color="bg-blue-500/10 text-blue-600" />
          <StatCard icon={UserCheck} label="Freelancers online"   value={g.freelancers_group?.online_now ?? 0} color="bg-pink-500/10 text-pink-600" />
        </div>
      </section>

      <section data-testid="stats-investors-group">
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Investidores</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Landmark} label="Interesses"          value={g.investors_group?.interests_total ?? g.investment_interests} color="bg-blue-500/10 text-blue-600" />
          <StatCard icon={Sparkles} label="Novos"                value={g.investors_group?.interests_new ?? 0}                       color="bg-indigo-500/10 text-indigo-600" />
          <StatCard icon={Award}    label="Aprovados"            value={g.investors_group?.interests_approved ?? 0}                  color="bg-emerald-500/10 text-emerald-600" />
          <StatCard icon={Users2}   label="Investidores únicos"   value={g.investors_group?.unique_investors ?? 0}                    color="bg-slate-500/10 text-slate-600" />
        </div>
      </section>

      <section data-testid="stats-wallet-group">
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Carteira & saques</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Coins}    label="Moedas em circulação" value={g.wallet_group?.coins_in_circulation ?? 0} color="bg-yellow-500/10 text-yellow-600" />
          <StatCard icon={Wallet}   label="Saldo total em carteira" value={brl(g.wallet_group?.brl_in_wallets)}    color="bg-emerald-500/10 text-emerald-600" />
          <StatCard icon={Banknote} label="Saques pendentes"     value={g.withdrawals_group?.pending ?? 0}         color="bg-orange-500/10 text-orange-500" />
          <StatCard icon={Check}    label="Saques pagos"         value={g.withdrawals_group?.paid ?? 0}            color="bg-blue-500/10 text-blue-600" />
        </div>
      </section>

      <section data-testid="stats-engagement-group">
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">Engajamento & receita</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={MessagesSquare} label="Postagens"             value={g.engagement_group?.posts_total ?? g.posts} color="bg-blue-500/10 text-blue-600" />
          <StatCard icon={Activity}       label="Posts (7 dias)"         value={g.engagement_group?.posts_7d ?? 0}         color="bg-indigo-500/10 text-indigo-600" />
          <StatCard icon={DollarSign}     label="Receita bruta"          value={brl(g.gross_revenue)}                       color="bg-green-500/10 text-green-600" />
          <StatCard icon={Percent}        label="Receita da plataforma"  value={brl(g.platform_revenue)}                    color="bg-emerald-500/10 text-emerald-600" />
        </div>
      </section>

      {/* Public counter reset */}
      <Card className="p-5 border-primary/40 bg-primary/5" data-testid="public-counter-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Users2 className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-widest text-primary font-bold">Contador público (landing)</div>
            <div className="text-2xl font-bold tracking-tight mt-1" data-testid="public-counter-value">
              {publicStats ? publicStats.total_users.toLocaleString("pt-BR") : "—"} <span className="text-sm text-muted-foreground font-normal">usuários visíveis</span>
            </div>
            {epoch ? (
              <div className="text-[11px] text-muted-foreground mt-1" data-testid="public-counter-epoch">
                Contando cadastros desde <span className="font-mono">{new Date(epoch).toLocaleString("pt-BR")}</span> (total real no banco: {stats.users}).
              </div>
            ) : (
              <div className="text-[11px] text-muted-foreground mt-1">Sem reset ativo — contando todos os usuários do banco.</div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              className="rounded-full gap-1"
              onClick={resetCounter}
              disabled={resetting}
              data-testid="reset-users-counter-btn"
            >
              Zerar contador
            </Button>
            {epoch && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={clearReset}
                disabled={resetting}
                data-testid="clear-users-counter-reset-btn"
              >
                Restaurar total geral
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Rides counter reset */}
      <Card className="p-5 border-orange-500/40 bg-orange-500/5" data-testid="rides-counter-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-orange-500 text-white flex items-center justify-center">
            <Car className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-widest text-orange-600 font-bold">Contador de corridas (landing)</div>
            <div className="text-2xl font-bold tracking-tight mt-1" data-testid="rides-counter-value">
              {publicStats ? publicStats.total_rides_completed.toLocaleString("pt-BR") : "—"} <span className="text-sm text-muted-foreground font-normal">corridas visíveis</span>
            </div>
            {ridesEpoch ? (
              <div className="text-[11px] text-muted-foreground mt-1" data-testid="rides-counter-epoch">
                Contando corridas desde <span className="font-mono">{new Date(ridesEpoch).toLocaleString("pt-BR")}</span> (total real no banco: {stats.rides_completed}).
              </div>
            ) : (
              <div className="text-[11px] text-muted-foreground mt-1">Sem reset ativo — contando todas as corridas concluídas.</div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              className="rounded-full gap-1"
              onClick={resetRidesCounter}
              disabled={resetting}
              data-testid="reset-rides-counter-btn"
            >
              Zerar corridas
            </Button>
            {ridesEpoch && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={clearRidesReset}
                disabled={resetting}
                data-testid="clear-rides-counter-reset-btn"
              >
                Restaurar total geral
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Receita bruta</div>
          <div className="text-3xl font-bold tracking-tight mt-1">R$ {stats.gross_revenue.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground mt-1">Somatório de corridas concluídas</div>
        </Card>
        <Card className="p-5 border-primary/40 bg-primary/5">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Receita plataforma ({stats.commission_pct}%)</div>
          <div className="text-3xl font-bold tracking-tight mt-1 text-primary">R$ {stats.platform_revenue.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground mt-1">Comissão retida em corridas concluídas</div>
        </Card>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/feed" replace />;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
          <Shield className="w-6 h-6 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Painel Administrativo</h1>
          <p className="text-sm text-muted-foreground">Gerencie a plataforma FUTURO PDV</p>
        </div>
      </div>

      <Tabs defaultValue="stats" className="w-full">
        <TabsList className="rounded-full flex flex-wrap w-full max-w-5xl gap-1 h-auto p-1">
          <TabsTrigger value="stats" data-testid="admin-tab-stats" className="rounded-full gap-1 text-xs"><TrendingUp className="w-4 h-4" />Visão</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-full gap-1 text-xs"><Percent className="w-4 h-4" />Taxas</TabsTrigger>
          <TabsTrigger value="finance" data-testid="admin-tab-finance" className="rounded-full gap-1 text-xs"><Landmark className="w-4 h-4" />Financeiro</TabsTrigger>
          <TabsTrigger value="pix" data-testid="admin-tab-pix" className="rounded-full gap-1 text-xs"><QrCode className="w-4 h-4" />PIX Pendentes</TabsTrigger>
          <TabsTrigger value="coins" data-testid="admin-tab-coins" className="rounded-full gap-1 text-xs"><Coins className="w-4 h-4" />FUTURO PDV</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="admin-tab-coupons" className="rounded-full gap-1 text-xs"><Ticket className="w-4 h-4" />Cupons</TabsTrigger>
          <TabsTrigger value="promos" data-testid="admin-tab-promos" className="rounded-full gap-1 text-xs"><Megaphone className="w-4 h-4" />Promoções</TabsTrigger>
          <TabsTrigger value="ads" data-testid="admin-tab-ads" className="rounded-full gap-1 text-xs"><Rocket className="w-4 h-4" />Anúncios</TabsTrigger>
          <TabsTrigger value="house-ads" data-testid="admin-tab-house-ads" className="rounded-full gap-1 text-xs"><Sparkles className="w-4 h-4" />Anúncios FUTURO PDV</TabsTrigger>
          <TabsTrigger value="missions" data-testid="admin-tab-missions" className="rounded-full gap-1 text-xs"><Target className="w-4 h-4" />Missões</TabsTrigger>
          <TabsTrigger value="post-reports" data-testid="admin-tab-post-reports" className="rounded-full gap-1 text-xs"><Flag className="w-4 h-4" />Denúncias</TabsTrigger>
          <TabsTrigger value="reactions" data-testid="admin-tab-reactions" className="rounded-full gap-1 text-xs">😍 Reações</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full gap-1 text-xs"><User className="w-4 h-4" />Usuários</TabsTrigger>
          <TabsTrigger value="investors" data-testid="admin-tab-investors" className="rounded-full gap-1 text-xs"><Award className="w-4 h-4" />Sócios</TabsTrigger>
          <TabsTrigger value="work" data-testid="admin-tab-work" className="rounded-full gap-1 text-xs"><Briefcase className="w-4 h-4" />Trabalhos</TabsTrigger>
          <TabsTrigger value="tiers" data-testid="admin-tab-tiers" className="rounded-full gap-1 text-xs"><Award className="w-4 h-4" />Categorias</TabsTrigger>
          <TabsTrigger value="jobs-mgmt" data-testid="admin-tab-jobs-mgmt" className="rounded-full gap-1 text-xs"><Briefcase className="w-4 h-4" />Vagas & Freelas</TabsTrigger>
          <TabsTrigger value="site-texts" data-testid="admin-tab-site-texts" className="rounded-full gap-1 text-xs"><Megaphone className="w-4 h-4" />Textos & Suporte</TabsTrigger>
          <TabsTrigger value="landing-content" data-testid="admin-tab-landing-content" className="rounded-full gap-1 text-xs"><Sparkles className="w-4 h-4" />Landing</TabsTrigger>
          <TabsTrigger value="sounds" data-testid="admin-tab-sounds" className="rounded-full gap-1 text-xs"><Volume2 className="w-4 h-4" />Sons</TabsTrigger>
          <TabsTrigger value="vehicle-cats" data-testid="admin-tab-vehicle-cats" className="rounded-full gap-1 text-xs"><CarFront className="w-4 h-4" />Categorias Veículo</TabsTrigger>
          <TabsTrigger value="driver-docs" data-testid="admin-tab-driver-docs" className="rounded-full gap-1 text-xs"><ShieldAlert className="w-4 h-4" />Docs Motoristas</TabsTrigger>
          <TabsTrigger value="moderation" data-testid="admin-tab-moderation" className="rounded-full gap-1 text-xs"><Eye className="w-4 h-4" />Moderação</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="admin-tab-disputes" className="rounded-full gap-1 text-xs"><AlertTriangle className="w-4 h-4" />Disputas</TabsTrigger>
          <TabsTrigger value="ads-manager-review" data-testid="admin-tab-ads-manager-review" className="rounded-full gap-1 text-xs"><Rocket className="w-4 h-4" />Ads Manager (revisão)</TabsTrigger>
          <TabsTrigger value="ads-manager-slots" data-testid="admin-tab-ads-manager-slots" className="rounded-full gap-1 text-xs"><Megaphone className="w-4 h-4" />Banners Laterais</TabsTrigger>
          <TabsTrigger value="qa-wallet" data-testid="admin-tab-qa-wallet" className="rounded-full gap-1 text-xs border border-red-500/40 text-red-600 data-[state=active]:bg-red-600 data-[state=active]:text-white"><FlaskConical className="w-4 h-4" />QA Wallet</TabsTrigger>
          <TabsTrigger value="branding" data-testid="admin-tab-branding" className="rounded-full gap-1 text-xs"><Smartphone className="w-4 h-4" />Ícone do App</TabsTrigger>
          {user?.is_super_admin && (
            <TabsTrigger value="admins" data-testid="admin-tab-admins" className="rounded-full gap-1 text-xs"><Shield className="w-4 h-4" />Administradores</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="stats" className="mt-6"><StatsTab /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab /></TabsContent>
        <TabsContent value="finance" className="mt-6"><FinanceTab /></TabsContent>
        <TabsContent value="pix" className="mt-6"><PixPendingTab /></TabsContent>
        <TabsContent value="coins" className="mt-6"><CoinsTab /></TabsContent>
        <TabsContent value="coupons" className="mt-6"><CouponsTab /></TabsContent>
        <TabsContent value="promos" className="mt-6"><PromotionsTab /></TabsContent>
        <TabsContent value="ads" className="mt-6"><AdsTab /></TabsContent>
        <TabsContent value="house-ads" className="mt-6"><HouseAdsTab /></TabsContent>
        <TabsContent value="missions" className="mt-6"><MissionsTab /></TabsContent>
        <TabsContent value="post-reports" className="mt-6"><PostReportsTab /></TabsContent>
        <TabsContent value="reactions" className="mt-6"><ReactionsAnalyticsCard /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="investors" className="mt-6"><InvestorsTab /></TabsContent>
        <TabsContent value="work" className="mt-6"><WorkTab /></TabsContent>
        <TabsContent value="tiers" className="mt-6"><TiersTab /></TabsContent>
        <TabsContent value="jobs-mgmt" className="mt-6"><JobsMgmtTab /></TabsContent>
        <TabsContent value="site-texts" className="mt-6"><SiteTextsTab /></TabsContent>
        <TabsContent value="landing-content" className="mt-6"><AdminLandingContent /></TabsContent>
        <TabsContent value="sounds" className="mt-6"><SoundsTab /></TabsContent>
        <TabsContent value="vehicle-cats" className="mt-6"><VehicleCategoriesTab /></TabsContent>
        <TabsContent value="driver-docs" className="mt-6"><DriverDocsTab /></TabsContent>
        <TabsContent value="moderation" className="mt-6"><ModerationTab /></TabsContent>
        <TabsContent value="disputes" className="mt-6"><AdminDisputesTab /></TabsContent>
        <TabsContent value="ads-manager-review" className="mt-6"><AdsManagerReviewTab /></TabsContent>
        <TabsContent value="ads-manager-slots" className="mt-6"><AdsManagerSlotsTab /></TabsContent>
        <TabsContent value="qa-wallet" className="mt-6"><AdminQAWalletTab /></TabsContent>
        <TabsContent value="branding" className="mt-6"><AdminBrandingTab /></TabsContent>
        {user?.is_super_admin && (
          <TabsContent value="admins" className="mt-6"><AdminsTab /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ---------- Finance tab (bank info) ----------
function FinanceTab() {
  const [form, setForm] = useState({
    holder_name: "", cnpj: "", bank_name: "", bank_code: "",
    agency: "", account_number: "", account_type: "checking",
    pix_key: "", pix_key_type: "cnpj",
  });
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/bank-info`).then((r) => {
      setForm(f => ({ ...f, ...r.data }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/bank-info`, form);
      toast.success("Dados bancários salvos com segurança!");
    } catch { toast.error("Erro ao salvar"); }
    setLoading(false);
  };

  const copyPix = async () => {
    if (!form.pix_key) return;
    try {
      await navigator.clipboard.writeText(form.pix_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("[Admin] copy PIX failed", err);
      toast.error("Não foi possível copiar a chave PIX");
    }
  };

  const bankAccountFilled = form.bank_name && form.account_number;
  const pixFilled = form.pix_key;

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6 max-w-6xl">
      <div className="lg:col-span-2"><AdminWithdrawGateCard /></div>
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center">
            <Landmark className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">Dados bancários da plataforma</h2>
            <p className="text-xs text-muted-foreground">Usados para recebimentos e prestação de contas</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">Titular</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome do titular / razão social</Label>
                <Input data-testid="bank-holder" placeholder="FUTURO PDV LTDA" value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">CNPJ / CPF</Label>
                <Input data-testid="bank-cnpj" placeholder="00.000.000/0000-00" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} />
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">Conta bancária</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome do banco</Label>
                <Input data-testid="bank-name" placeholder="Ex: Nubank, Itaú, Bradesco" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Código do banco</Label>
                <Input data-testid="bank-code" placeholder="Ex: 260" value={form.bank_code} onChange={(e) => setForm({ ...form, bank_code: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Agência</Label>
                <Input data-testid="bank-agency" placeholder="0001" value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Número da conta</Label>
                <Input data-testid="bank-account" placeholder="12345-6" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Tipo de conta</Label>
                <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                  <SelectTrigger data-testid="bank-account-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Conta corrente</SelectItem>
                    <SelectItem value="savings">Poupança</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">PIX</div>
            <div className="grid md:grid-cols-[160px_1fr] gap-3">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={form.pix_key_type} onValueChange={(v) => setForm({ ...form, pix_key_type: v })}>
                  <SelectTrigger data-testid="bank-pix-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cnpj">CNPJ</SelectItem>
                    <SelectItem value="cpf">CPF</SelectItem>
                    <SelectItem value="email">E-mail</SelectItem>
                    <SelectItem value="phone">Telefone</SelectItem>
                    <SelectItem value="random">Chave aleatória</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Chave PIX</Label>
                <Input data-testid="bank-pix-key" placeholder="Digite a chave" value={form.pix_key} onChange={(e) => setForm({ ...form, pix_key: e.target.value })} />
              </div>
            </div>
          </div>

          <Button data-testid="bank-save" onClick={save} disabled={loading} className="rounded-full">
            {loading ? "Salvando..." : "Salvar dados bancários"}
          </Button>
        </div>
      </Card>

      {/* Preview panel */}
      <div className="space-y-4">
        <Card className="p-5 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">Prévia</div>
          {!bankAccountFilled && !pixFilled ? (
            <p className="text-sm text-muted-foreground">Preencha os dados para ver a prévia</p>
          ) : (
            <div className="space-y-3">
              {form.holder_name && (
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Titular</div>
                  <div className="font-semibold text-sm">{form.holder_name}</div>
                  {form.cnpj && <div className="text-xs text-muted-foreground font-mono">{form.cnpj}</div>}
                </div>
              )}
              {bankAccountFilled && (
                <div className="p-3 rounded-lg bg-muted/50">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Conta bancária</div>
                  <div className="font-bold text-sm">{form.bank_name}{form.bank_code && ` (${form.bank_code})`}</div>
                  <div className="text-xs text-muted-foreground font-mono mt-1">
                    Ag. {form.agency || "---"} · Conta {form.account_number}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {form.account_type === "checking" ? "Conta corrente" : "Poupança"}
                  </div>
                </div>
              )}
              {pixFilled && (
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] text-primary uppercase tracking-widest font-bold">PIX · {form.pix_key_type.toUpperCase()}</div>
                    <button onClick={copyPix} className="text-xs text-primary hover:underline flex items-center gap-1">
                      {copied ? <><Check className="w-3 h-3" /> Copiado</> : <><Copy className="w-3 h-3" /> Copiar</>}
                    </button>
                  </div>
                  <div className="font-mono text-sm break-all">{form.pix_key}</div>
                </div>
              )}
            </div>
          )}
        </Card>
        <Card className="p-4 bg-yellow-500/10 border-yellow-500/30">
          <div className="text-xs">
            <strong>🔒 Segurança:</strong> apenas administradores da plataforma acessam esta seção. Os dados são armazenados de forma privada e usados exclusivamente para operações financeiras.
          </div>
        </Card>
      </div>
    </div>
  );
}

// ---------- Ads admin tab ----------
function AdsTab() {
  const [packages, setPackages] = useState([]);
  const [ads, setAds] = useState([]);
  const [cpm, setCpm] = useState("");

  const load = async () => {
    const [p, a, c] = await Promise.all([
      axios.get(`${API}/admin/boost-packages`),
      axios.get(`${API}/admin/ads`),
      axios.get(`${API}/admin/ad-pricing`),
    ]);
    setPackages(p.data);
    setAds(a.data);
    setCpm(c.data.cpm_brl);
  };
  useEffect(() => { load(); }, []);

  const saveCpm = async () => {
    try {
      const r = await axios.put(`${API}/admin/ad-pricing`, { cpm_brl: parseFloat(cpm) });
      setCpm(r.data.cpm_brl);
      toast.success("CPM atualizado!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const addPkg = () => setPackages([...packages, { package_id: `pkg_${Date.now()}`, name: "Novo pacote", impressions: 1000, price_brl: 19.9, days: 3 }]);
  const removePkg = (i) => setPackages(packages.filter((_, idx) => idx !== i));
  const updatePkg = (i, field, val) => setPackages(packages.map((p, idx) => idx === i ? { ...p, [field]: val } : p));

  const savePkgs = async () => {
    try {
      await axios.put(`${API}/admin/boost-packages`, packages.map(p => ({
        name: p.name,
        impressions: parseInt(p.impressions) || 1,
        price_brl: parseFloat(p.price_brl) || 0.01,
        days: parseInt(p.days) || 1,
      })));
      toast.success("Pacotes atualizados!");
      load();
    } catch { toast.error("Erro"); }
  };

  const removeAd = async (id) => {
    if (!window.confirm("Excluir anúncio?")) return;
    await axios.delete(`${API}/ads/${id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Rocket className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">Pacotes de impulsionamento</h2>
              <p className="text-xs text-muted-foreground">Configure valores e quantidade de impressões oferecidas</p>
            </div>
          </div>
          <Button onClick={addPkg} data-testid="admin-pkg-add" size="sm" variant="outline" className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Adicionar pacote
          </Button>
        </div>
        <div className="space-y-3">
          {packages.map((p, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_100px_auto] gap-2 items-center" data-testid={`admin-pkg-${i}`}>
              <Input placeholder="Nome" value={p.name} onChange={(e) => updatePkg(i, "name", e.target.value)} />
              <Input type="number" placeholder="Impressões" value={p.impressions} onChange={(e) => updatePkg(i, "impressions", e.target.value)} />
              <Input type="number" step="0.01" placeholder="Preço R$" value={p.price_brl} onChange={(e) => updatePkg(i, "price_brl", e.target.value)} />
              <Input type="number" placeholder="Dias" value={p.days} onChange={(e) => updatePkg(i, "days", e.target.value)} />
              <Button variant="ghost" size="icon" onClick={() => removePkg(i)}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
        <Button data-testid="admin-pkg-save" onClick={savePkgs} className="rounded-full mt-4">Salvar pacotes</Button>
      </Card>

      <Card className="p-5" data-testid="admin-ad-pricing-card">
        <h2 className="text-lg font-bold tracking-tight">Preço da veiculação por orçamento (CPM)</h2>
        <p className="text-xs text-muted-foreground mb-3">Valor cobrado do saldo do anunciante a cada 1.000 impressões quando o anúncio roda com orçamento diário ou por período.</p>
        <div className="flex items-center gap-2 max-w-sm">
          <span className="text-sm text-muted-foreground">R$</span>
          <Input type="number" step="0.01" min="0.5" value={cpm} onChange={(e) => setCpm(e.target.value)} data-testid="admin-ad-cpm-input" />
          <span className="text-xs text-muted-foreground whitespace-nowrap">/ 1.000 impr.</span>
          <Button onClick={saveCpm} className="rounded-full" data-testid="admin-ad-cpm-save">Salvar</Button>
        </div>
      </Card>

      <div>
        <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">
          Todos os anúncios ({ads.length})
        </h3>
        <div className="space-y-3">
          {ads.length === 0 && <Card className="p-8 text-center text-muted-foreground">Nenhum anúncio criado</Card>}
          {ads.map((a) => {
            const isBoosted = a.boost_expires_at && a.boost_expires_at > new Date().toISOString();
            return (
              <Card key={a.ad_id} className="p-4 flex gap-3 items-center" data-testid={`admin-ad-${a.ad_id}`}>
                {a.image_url && <img src={a.image_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm truncate">{a.title}</span>
                    {isBoosted && <Badge className="bg-primary text-[10px]">Impulsionado</Badge>}
                    <span className="text-xs text-muted-foreground">por {a.author_name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {a.impressions || 0} impressões · {a.clicks || 0} cliques
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeAd(a.ad_id)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- House Ads admin tab (free Futuro PDV sponsored ads) ----------
const EMPTY_HOUSE_FORM = { title: "", description: "", image_url: "", target_url: "", cta: "Saiba mais", target_cities: "", target_interests: "" };

function HouseAdsTab() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_HOUSE_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ads/house`);
      setAds(r.data || []);
    } catch { toast.error("Erro ao carregar"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (a) => {
    setEditingId(a.ad_id);
    setForm({
      title: a.title || "",
      description: a.description || "",
      image_url: a.image_url || "",
      target_url: a.target_url || "",
      cta: a.cta || "Saiba mais",
      target_cities: (a.target_cities || []).join(", "),
      target_interests: (a.target_interests || []).join(", "),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_HOUSE_FORM);
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    description: form.description.trim(),
    image_url: form.image_url.trim() || null,
    target_url: form.target_url.trim() || null,
    cta: form.cta.trim() || "Saiba mais",
    target_cities: form.target_cities.split(",").map((s) => s.trim()).filter(Boolean),
    target_interests: form.target_interests.split(",").map((s) => s.trim()).filter(Boolean),
    target_genders: [],
  });

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error("Preencha título e descrição");
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/admin/ads/${editingId}`, buildPayload());
        toast.success("Anúncio atualizado!");
      } else {
        await axios.post(`${API}/admin/ads/house`, buildPayload());
        toast.success("Anúncio Futuro PDV publicado!");
      }
      cancelEdit();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  const togglePause = async (a) => {
    try {
      await axios.patch(`${API}/admin/ads/${a.ad_id}`, { active: !a.active });
      toast.success(!a.active ? "Anúncio reativado" : "Anúncio pausado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir anúncio Futuro PDV?")) return;
    try {
      await axios.delete(`${API}/ads/${id}`);
      toast.success("Excluído");
      if (editingId === id) cancelEdit();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-house-ads-panel">
      <Card className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Anúncios patrocinados Futuro PDV</h2>
            <p className="text-xs text-muted-foreground">
              Gratuitos — aparecem em todas as páginas com selo "Patrocinado". Sem moderação, sem cobrança.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3" data-testid="house-ad-form">
          <Input
            data-testid="house-ad-title"
            placeholder="Título"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <Input
            data-testid="house-ad-cta"
            placeholder="Botão (ex: Baixe agora)"
            value={form.cta}
            onChange={(e) => setForm({ ...form, cta: e.target.value })}
          />
          <Textarea
            data-testid="house-ad-description"
            className="md:col-span-2"
            rows={3}
            placeholder="Descrição"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <div className="flex gap-2 md:col-span-2">
            <Input
              data-testid="house-ad-image"
              placeholder="URL da imagem"
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            />
            <ImageUploader
              testid="house-ad-upload-btn"
              size="default"
              label="Foto"
              onUploaded={(url) => setForm({ ...form, image_url: url })}
            />
          </div>
          {form.image_url && (
            <img src={form.image_url} alt="preview" className="rounded-lg max-h-32 object-cover md:col-span-2" data-testid="house-ad-preview" />
          )}
          <Input
            data-testid="house-ad-target-url"
            placeholder="Link de destino (https://...)"
            value={form.target_url}
            onChange={(e) => setForm({ ...form, target_url: e.target.value })}
          />
          <Input
            data-testid="house-ad-target-cities"
            placeholder="Cidades alvo (opcional, separe por vírgula)"
            value={form.target_cities}
            onChange={(e) => setForm({ ...form, target_cities: e.target.value })}
          />
          <Input
            data-testid="house-ad-target-interests"
            className="md:col-span-2"
            placeholder="Interesses alvo (opcional, separe por vírgula)"
            value={form.target_interests}
            onChange={(e) => setForm({ ...form, target_interests: e.target.value })}
          />
        </div>

        <div className="flex gap-2 mt-4">
          <Button
            data-testid="house-ad-save"
            onClick={save}
            disabled={saving}
            className="rounded-full gap-1"
          >
            {editingId ? <><Pencil className="w-4 h-4" /> Salvar alterações</> : <><Plus className="w-4 h-4" /> Publicar anúncio</>}
          </Button>
          {editingId && (
            <Button
              data-testid="house-ad-cancel"
              variant="outline"
              onClick={cancelEdit}
              className="rounded-full"
            >
              Cancelar
            </Button>
          )}
        </div>
      </Card>

      <div>
        <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">
          Anúncios ativos e pausados ({ads.length})
        </h3>
        {loading && <Card className="p-6 text-center text-sm text-muted-foreground">Carregando...</Card>}
        {!loading && ads.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground" data-testid="house-ads-empty">
            Nenhum anúncio Futuro PDV criado ainda.
          </Card>
        )}
        <div className="space-y-3">
          {ads.map((a) => (
            <Card
              key={a.ad_id}
              className={`p-4 flex gap-3 ${!a.active ? "opacity-60" : ""}`}
              data-testid={`house-ad-${a.ad_id}`}
            >
              {a.image_url && <img src={a.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm truncate">{a.title}</span>
                  <Badge className="bg-primary text-[10px] gap-1">
                    <Sparkles className="w-3 h-3" /> Patrocinado
                  </Badge>
                  {!a.active && (
                    <Badge variant="outline" className="text-[10px]" data-testid={`house-ad-paused-badge-${a.ad_id}`}>Pausado</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{a.description}</p>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {a.impressions || 0} impressões · {a.clicks || 0} cliques
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full gap-1 h-8 text-xs"
                  onClick={() => startEdit(a)}
                  data-testid={`house-ad-edit-${a.ad_id}`}
                >
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full gap-1 h-8 text-xs"
                  onClick={() => togglePause(a)}
                  data-testid={`house-ad-toggle-${a.ad_id}`}
                >
                  {a.active ? <><Pause className="w-3.5 h-3.5" /> Pausar</> : <><Play className="w-3.5 h-3.5" /> Reativar</>}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-full gap-1 h-8 text-xs"
                  onClick={() => remove(a.ad_id)}
                  data-testid={`house-ad-delete-${a.ad_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Missions admin tab (daily missions with coin rewards) ----------
const EMPTY_MISSION_FORM = { title: "", description: "", action: "ride_completed_driver", target: 3, coin_reward: 50, icon: "🎯", active: true };

function MissionsTab() {
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [form, setForm] = useState(EMPTY_MISSION_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [i, a] = await Promise.all([
        axios.get(`${API}/admin/missions`),
        axios.get(`${API}/missions/actions`),
      ]);
      setItems(i.data || []);
      setActions(a.data || []);
    } catch { toast.error("Erro ao carregar"); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (m) => {
    setEditingId(m.mission_id);
    setForm({
      title: m.title || "",
      description: m.description || "",
      action: m.action,
      target: m.target,
      coin_reward: m.coin_reward,
      icon: m.icon || "🎯",
      active: !!m.active,
    });
  };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY_MISSION_FORM); };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Informe um título");
    if (!form.action) return toast.error("Selecione uma ação");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        action: form.action,
        target: Math.max(1, parseInt(form.target) || 1),
        coin_reward: Math.max(1, parseInt(form.coin_reward) || 1),
        icon: form.icon || "🎯",
        active: !!form.active,
      };
      if (editingId) {
        await axios.patch(`${API}/admin/missions/${editingId}`, payload);
        toast.success("Missão atualizada");
      } else {
        await axios.post(`${API}/admin/missions`, payload);
        toast.success("Missão criada!");
      }
      cancelEdit();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  const toggle = async (m) => {
    try {
      await axios.patch(`${API}/admin/missions/${m.mission_id}`, { active: !m.active });
      load();
    } catch { toast.error("Erro"); }
  };
  const del = async (id) => {
    if (!window.confirm("Excluir missão?")) return;
    await axios.delete(`${API}/admin/missions/${id}`);
    if (editingId === id) cancelEdit();
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-missions-panel">
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Target className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Missões diárias</h2>
            <p className="text-xs text-muted-foreground">Configure tarefas que os usuários cumprem para ganhar moedas FUTURO PDV.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Input data-testid="mission-title" placeholder="Título (ex: Faça 3 corridas hoje)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input data-testid="mission-icon" placeholder="Ícone (emoji) 🎯" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} maxLength={4} />
          <Textarea data-testid="mission-description" className="md:col-span-2" rows={2} placeholder="Descrição curta (opcional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="md:col-span-2">
            <Label className="text-xs text-muted-foreground">Ação monitorada</Label>
            <select
              data-testid="mission-action"
              value={form.action}
              onChange={(e) => setForm({ ...form, action: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1"
            >
              {actions.map((a) => (
                <option key={a.key} value={a.key}>{a.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Meta (quantidade)</Label>
            <Input data-testid="mission-target" type="number" min="1" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Recompensa (moedas)</Label>
            <Input data-testid="mission-reward" type="number" min="1" value={form.coin_reward} onChange={(e) => setForm({ ...form, coin_reward: e.target.value })} />
          </div>
          <div className="flex items-center gap-2 md:col-span-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="mission-active" />
            <span className="text-sm">Missão ativa</span>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button data-testid="mission-save" onClick={submit} disabled={saving} className="rounded-full gap-1">
            {editingId ? <><Pencil className="w-4 h-4" /> Salvar alterações</> : <><Plus className="w-4 h-4" /> Criar missão</>}
          </Button>
          {editingId && <Button data-testid="mission-cancel" variant="outline" onClick={cancelEdit} className="rounded-full">Cancelar</Button>}
        </div>
      </Card>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Todas as missões ({items.length})</h3>
        {items.length === 0 && <Card className="p-8 text-center text-muted-foreground" data-testid="missions-empty">Nenhuma missão criada ainda.</Card>}
        {items.map((m) => {
          const actionLabel = actions.find((a) => a.key === m.action)?.label || m.action;
          return (
            <Card key={m.mission_id} className={`p-4 flex gap-3 ${!m.active ? "opacity-60" : ""}`} data-testid={`admin-mission-${m.mission_id}`}>
              <div className="text-3xl leading-none">{m.icon || "🎯"}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm truncate">{m.title}</span>
                  {!m.active && <Badge variant="outline" className="text-[10px]">Inativa</Badge>}
                </div>
                {m.description && <p className="text-xs text-muted-foreground line-clamp-2">{m.description}</p>}
                <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-3">
                  <span>Ação: <span className="font-semibold">{actionLabel}</span></span>
                  <span>Meta: <span className="font-semibold">{m.target}</span></span>
                  <span className="flex items-center gap-1"><Gift className="w-3 h-3 text-primary" /> +{m.coin_reward} moedas</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="outline" className="rounded-full h-8 text-xs gap-1" onClick={() => startEdit(m)} data-testid={`mission-edit-${m.mission_id}`}>
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </Button>
                <Button size="sm" variant="outline" className="rounded-full h-8 text-xs gap-1" onClick={() => toggle(m)} data-testid={`mission-toggle-${m.mission_id}`}>
                  {m.active ? <><Pause className="w-3.5 h-3.5" /> Pausar</> : <><Play className="w-3.5 h-3.5" /> Ativar</>}
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => del(m.mission_id)} data-testid={`mission-delete-${m.mission_id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Post Reports admin tab ----------
const REPORT_REASON_LABEL = {
  spam: "Spam",
  offensive: "Ofensivo",
  violence: "Violência",
  fraud: "Fraude",
  other: "Outro",
};

function UserReportsSection() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = async () => { try { setItems((await axios.get(`${API}/admin/user-reports?status=${status}`)).data || []); } catch { /* noop */ } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);
  const act = async (id, action) => {
    try { await axios.post(`${API}/admin/user-reports/${id}/${action}`); toast.success(action === "dismiss" ? "Dispensada" : "Resolvida"); load(); }
    catch { toast.error("Erro"); }
  };
  return (
    <Card className="p-4 space-y-3" data-testid="admin-user-reports">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-md font-bold tracking-tight">Denúncias de usuários</h2>
          <p className="text-xs text-muted-foreground">{items.length} {status === "pending" ? "pendentes" : "no filtro"}</p>
        </div>
        <div className="inline-flex rounded-full bg-muted p-0.5 text-xs">
          {[["pending", "Pendentes"], ["resolved", "Resolvidas"], ["dismissed", "Dispensadas"], ["all", "Todas"]].map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1 rounded-full ${status === k ? "bg-background shadow font-semibold" : ""}`} data-testid={`user-reports-filter-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      {items.length === 0 ? <p className="text-xs text-muted-foreground">Nenhuma denúncia.</p> : items.map((r) => (
        <div key={r.report_id} className="flex items-center gap-3 p-2 rounded-lg border" data-testid={`user-report-${r.report_id}`}>
          <Avatar className="w-9 h-9"><AvatarImage src={r.target?.picture} /><AvatarFallback>{r.target?.name?.[0] || "?"}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0 text-sm">
            <Link to={`/user/${r.target_id}`} className="font-semibold hover:underline">{r.target?.name || r.target_id}</Link>
            <span className="text-muted-foreground"> · denunciado por {r.reporter_name} · <b>{r.reason}</b></span>
            {r.details && <div className="text-xs text-muted-foreground truncate">“{r.details}”</div>}
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted">{r.status}</span>
          {r.status === "pending" && (
            <>
              <Button size="sm" variant="outline" onClick={() => act(r.report_id, "dismiss")} data-testid={`user-report-dismiss-${r.report_id}`}>Dispensar</Button>
              <Button size="sm" onClick={() => act(r.report_id, "resolve")} data-testid={`user-report-resolve-${r.report_id}`}>Resolver</Button>
            </>
          )}
        </div>
      ))}
    </Card>
  );
}


function PostReportsTab() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/post-reports?status=${filter}`);
      setReports(r.data || []);
    } catch { toast.error("Erro ao carregar"); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const dismiss = async (id) => {
    try {
      await axios.post(`${API}/admin/post-reports/${id}/dismiss`);
      toast.success("Denúncia dispensada");
      load();
    } catch { toast.error("Erro"); }
  };
  const removePost = async (id) => {
    if (!window.confirm("Remover o post denunciado? O autor será notificado.")) return;
    try {
      await axios.post(`${API}/admin/post-reports/${id}/remove-post`);
      toast.success("Post removido e autor notificado");
      load();
    } catch { toast.error("Erro"); }
  };

  const pendingCount = reports.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-4" data-testid="admin-post-reports-panel">
      <UserReportsSection />
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-destructive/15 text-destructive flex items-center justify-center">
          <Flag className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold tracking-tight">Denúncias de conteúdo</h2>
          <p className="text-xs text-muted-foreground">
            {filter === "pending"
              ? `${pendingCount} denúncias aguardando análise. Cada nova notifica todos os admins.`
              : "Filtrando por status selecionado."}
          </p>
        </div>
        <div className="flex gap-1">
          {[
            { key: "pending", label: "Pendentes" },
            { key: "dismissed", label: "Dispensadas" },
            { key: "post_removed", label: "Post removido" },
            { key: "all", label: "Todas" },
          ].map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? "default" : "outline"}
              className="rounded-full text-xs"
              onClick={() => setFilter(f.key)}
              data-testid={`report-filter-${f.key}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {loading && <Card className="p-6 text-center text-sm text-muted-foreground">Carregando...</Card>}
      {!loading && reports.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground" data-testid="reports-empty">
          <Flag className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Nenhuma denúncia neste filtro.
        </Card>
      )}

      <div className="space-y-3">
        {reports.map((r) => (
          <Card
            key={r.report_id}
            className={`p-4 space-y-3 ${r.status !== "pending" ? "opacity-60" : ""}`}
            data-testid={`report-${r.report_id}`}
          >
            <div className="flex items-start gap-3">
              {r.post_image_url && (
                <img src={r.post_image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="destructive" className="text-[10px]" data-testid={`report-reason-${r.report_id}`}>
                    {REPORT_REASON_LABEL[r.reason] || r.reason}
                  </Badge>
                  {r.post_report_count > 1 && (
                    <Badge className="bg-amber-500 text-white text-[10px]">
                      {r.post_report_count} denúncias
                    </Badge>
                  )}
                  {r.status === "dismissed" && <Badge variant="outline" className="text-[10px]">Dispensada</Badge>}
                  {r.status === "post_removed" && <Badge className="bg-emerald-600 text-white text-[10px]">Post removido</Badge>}
                  {r.status === "post_deleted" && <Badge variant="outline" className="text-[10px]">Autor excluiu</Badge>}
                  {!r.post_exists && <Badge variant="outline" className="text-[10px]">Post inexistente</Badge>}
                </div>
                <div className="text-sm font-semibold mt-1">
                  Post de <span className="text-primary">{r.post_author_name || "usuário"}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-3 whitespace-pre-wrap">
                  “{r.post_snippet || "(sem texto)"}”
                </p>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Denunciado por <span className="font-semibold">{r.reporter_name}</span> · {new Date(r.created_at).toLocaleString("pt-BR")}
                </div>
                {r.details && (
                  <div className="mt-2 p-2 rounded-md bg-muted text-xs">
                    <span className="font-semibold">Detalhes:</span> {r.details}
                  </div>
                )}
              </div>
            </div>
            {r.status === "pending" && r.post_exists && (
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => dismiss(r.report_id)}
                  data-testid={`report-dismiss-${r.report_id}`}
                >
                  Dispensar
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="rounded-full gap-1"
                  onClick={() => removePost(r.report_id)}
                  data-testid={`report-remove-post-${r.report_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Excluir post
                </Button>
              </div>
            )}
            {r.status === "pending" && !r.post_exists && (
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => dismiss(r.report_id)}
                  data-testid={`report-dismiss-${r.report_id}`}
                >
                  Dispensar
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------- Users tab (admin support view) ----------
function UsersTab() {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [data, setData] = useState({ total: 0, users: [] });
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [geo, setGeo] = useState({ country: "", state: "", city: "" });
  const onGeoFilter = useCallback((g) => setGeo((prev) => (prev.country === g.country && prev.state === g.state && prev.city === g.city ? prev : g)), []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (role) params.set("role", role);
      if (geo.country) params.set("country", geo.country);
      if (geo.state) params.set("state", geo.state);
      if (geo.city) params.set("city", geo.city);
      params.set("limit", "80");
      const r = await axios.get(`${API}/admin/users?${params}`);
      setData(r.data || { total: 0, users: [] });
    } catch { toast.error("Erro ao carregar"); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [role, geo.country, geo.state, geo.city]);
  // debounced search
  useEffect(() => {
    const id = setTimeout(load, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const openDetail = async (uid) => {
    setOpenId(uid);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${uid}/full`);
      setDetail(r.data);
    } catch { toast.error("Erro ao carregar dossiê"); }
    setDetailLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      <AdminGeoStats onFilter={onGeoFilter} />
      {(geo.country || geo.state || geo.city) && (
        <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="users-geo-active-filter">
          <span className="text-muted-foreground">Filtrando usuários por:</span>
          {[geo.country, geo.state, geo.city].filter(Boolean).map((v) => <span key={v} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{v}</span>)}
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setGeo({ country: "", state: "", city: "" })} data-testid="users-geo-clear">limpar</Button>
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <User className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <h2 className="text-lg font-bold tracking-tight">Suporte a usuários</h2>
          <p className="text-xs text-muted-foreground">Consulte o cadastro completo de qualquer usuário: perfil, documentos, wallet, atividades e histórico.</p>
        </div>
        <div className="flex items-center gap-1">
          {[
            { key: "", label: "Todos" },
            { key: "driver", label: "Motoristas" },
            { key: "investor", label: "Sócios" },
            { key: "admin", label: "Admins" },
          ].map((r) => (
            <Button
              key={r.key}
              size="sm"
              variant={role === r.key ? "default" : "outline"}
              className="rounded-full text-xs"
              onClick={() => setRole(r.key)}
              data-testid={`users-filter-${r.key || "all"}`}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="users-search"
          className="pl-9"
          placeholder="Buscar por nome, e-mail, telefone, CPF ou user_id..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="text-[11px] text-muted-foreground">{data.total.toLocaleString("pt-BR")} usuários</div>

      <Card className="overflow-hidden divide-y divide-border">
        {loading && <div className="p-6 text-center text-sm text-muted-foreground">Carregando...</div>}
        {!loading && data.users.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="users-empty">Nenhum usuário encontrado.</div>
        )}
        {data.users.map((u) => (
          <button
            key={u.user_id}
            onClick={() => openDetail(u.user_id)}
            data-testid={`user-row-${u.user_id}`}
            className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 text-left transition-colors"
          >
            <img
              src={u.picture || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(u.name || u.user_id)}`}
              alt=""
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              onError={(e) => { e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(u.name || u.user_id)}`; }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                <span className="truncate">{u.name || "Sem nome"}</span>
                {u.is_admin && <Badge className="bg-primary text-[10px]">Admin</Badge>}
                {u.is_driver && <Badge className="bg-orange-500 text-white text-[10px]">Motorista</Badge>}
                {u.is_investor && <Badge className="bg-emerald-500 text-white text-[10px]">Sócio</Badge>}
                {u.blocked && <Badge variant="destructive" className="text-[10px]">Bloqueado</Badge>}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{u.email} · {u.phone || "sem telefone"}{(u.city || u.state || u.country) ? ` · ${[u.city, u.state, u.country].filter(Boolean).join(", ")}` : ""}</div>
              <div className="text-[10px] text-muted-foreground font-mono truncate">{u.user_id} · {u.created_at ? new Date(u.created_at).toLocaleDateString("pt-BR") : "—"}</div>
            </div>
            <ClipboardList className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          </button>
        ))}
      </Card>

      {openId && (
        <UserDetailSheet
          userId={openId}
          detail={detail}
          loading={detailLoading}
          onClose={() => { setOpenId(null); setDetail(null); }}
          onRefresh={() => openDetail(openId)}
          onDeleted={() => load()}
        />
      )}
    </div>
  );
}

function UserDetailSheet({ userId, detail, loading, onClose, onRefresh, onDeleted }) {
  return (
    <div
      className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="user-detail-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl h-full bg-background overflow-y-auto shadow-2xl border-l border-border"
      >
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border p-4 flex items-center gap-3">
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center" data-testid="user-detail-close">
            <X className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dossiê do usuário</div>
            <div className="font-bold font-mono text-sm">{userId}</div>
          </div>
          <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={onRefresh}>Recarregar</Button>
        </div>

        {loading && <div className="p-10 text-center text-sm text-muted-foreground">Carregando dossiê...</div>}
        {!loading && detail && (
          <>
            <div className="px-5 pt-5">
              <AdminUserEditPanel profile={detail.profile || {}} onSaved={onRefresh} onDeleted={() => { onClose(); onDeleted?.(); }} />
            </div>
            <UserDetailContent detail={detail} />
          </>
        )}
      </div>
    </div>
  );
}

function KV({ k, v, mono = false, testid }) {
  return (
    <div className="flex gap-2 text-xs py-1 border-b border-dashed border-border last:border-0" data-testid={testid}>
      <div className="w-32 uppercase tracking-widest text-muted-foreground font-semibold text-[10px]">{k}</div>
      <div className={`flex-1 min-w-0 break-words ${mono ? "font-mono" : ""}`}>{v ?? "—"}</div>
    </div>
  );
}

function UserDetailContent({ detail }) {
  const p = detail.profile || {};
  const c = detail.counters || {};
  const tx = detail.recent_transactions || {};
  return (
    <div className="p-5 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <img
          src={p.picture || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(p.name || p.user_id)}`}
          alt=""
          className="w-16 h-16 rounded-2xl object-cover"
          onError={(e) => { e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(p.name || p.user_id)}`; }}
        />
        <div className="min-w-0 flex-1">
          <div className="text-lg font-bold truncate">{p.name || "Sem nome"}</div>
          <div className="flex items-center gap-1 flex-wrap mt-1">
            {p.is_admin && <Badge className="bg-primary text-[10px]">Admin</Badge>}
            {p.is_driver && <Badge className="bg-orange-500 text-white text-[10px]">Motorista</Badge>}
            {p.is_investor && <Badge className="bg-emerald-500 text-white text-[10px]">Sócio</Badge>}
            {p.blocked && <Badge variant="destructive" className="text-[10px]">Bloqueado</Badge>}
            {p.tier && <Badge variant="outline" className="text-[10px]">{p.tier}</Badge>}
          </div>
        </div>
      </div>

      {/* Wallet & counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card className="p-3">
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Saldo R$</div>
          <div className="text-lg font-bold" data-testid="detail-wallet-balance">R$ {Number(detail.wallet?.balance || 0).toFixed(2)}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Moedas</div>
          <div className="text-lg font-bold" data-testid="detail-coins">{Number(detail.wallet?.coins_balance || 0).toLocaleString("pt-BR")}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Corridas</div>
          <div className="text-lg font-bold">{c.rides_completed || 0}<span className="text-xs text-muted-foreground font-normal"> / {(c.rides_as_driver || 0) + (c.rides_as_rider || 0)}</span></div>
        </Card>
        <Card className="p-3">
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">Denúncias</div>
          <div className="text-lg font-bold" data-testid="detail-reports">{c.reports_against_user || 0} <span className="text-xs text-muted-foreground font-normal">recebidas</span></div>
        </Card>
      </div>

      {/* Profile */}
      <Section title="Perfil & cadastro" testid="detail-section-profile">
        <KV k="user_id" v={p.user_id} mono testid="detail-user-id" />
        <KV k="Nome" v={p.name} />
        <KV k="E-mail" v={p.email} />
        <KV k="Telefone" v={p.phone} />
        <KV k="CPF" v={p.cpf} />
        <KV k="Nascimento" v={p.birth_date} />
        <KV k="Cidade" v={p.city} />
        <KV k="Gênero" v={p.gender} />
        <KV k="Bio" v={p.bio} />
        <KV k="Interesses" v={(p.interests || []).join(", ")} />
        <KV k="Referido por" v={p.referred_by} mono />
        <KV k="Criado em" v={p.created_at ? new Date(p.created_at).toLocaleString("pt-BR") : "—"} />
        <KV k="Última atualização" v={p.updated_at ? new Date(p.updated_at).toLocaleString("pt-BR") : "—"} />
        <KV k="Confirmado" v={p.email_verified ? "Sim" : "Não"} />
        <KV k="Rating" v={p.avg_rating ? `${p.avg_rating.toFixed(1)} (${p.ratings_count || 0})` : "—"} />
      </Section>

      {/* Driver info */}
      {p.is_driver && (
        <Section title="Motorista" testid="detail-section-driver">
          <KV k="CNH" v={p.cnh_number} />
          <KV k="Validade CNH" v={p.cnh_expires} />
          <KV k="Placa" v={p.vehicle_plate} />
          <KV k="Veículo" v={p.vehicle_model} />
          <KV k="Cor" v={p.vehicle_color} />
          <KV k="Categoria" v={p.vehicle_type} />
          <KV k="Ano" v={p.vehicle_year} />
        </Section>
      )}

      {/* Docs */}
      <Section title={`Documentos (${(detail.docs || []).length})`} testid="detail-section-docs">
        {(detail.docs || []).length === 0 && (
          <div className="text-xs text-muted-foreground py-2">Nenhum documento enviado.</div>
        )}
        {(detail.docs || []).map((d) => (
          <div key={d.doc_id || d.file_id} className="flex items-center gap-2 py-1.5 border-b border-dashed border-border last:border-0" data-testid={`detail-doc-${d.doc_id || d.file_id || d.kind}`}>
            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="text-xs flex-1 min-w-0">
              <div className="font-semibold">{d.kind || d.type || "documento"}</div>
              <div className="text-[10px] text-muted-foreground">{d.status || "—"} · {d.created_at ? new Date(d.created_at).toLocaleString("pt-BR") : ""}</div>
            </div>
            {(d.file_url || d.url) && (
              <a href={d.file_url || d.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">
                Ver
              </a>
            )}
          </div>
        ))}
      </Section>

      {/* Vehicles */}
      {(detail.vehicles || []).length > 0 && (
        <Section title={`Veículos (${detail.vehicles.length})`} testid="detail-section-vehicles">
          {detail.vehicles.map((v, i) => (
            <div key={i} className="text-xs py-1">
              <span className="font-semibold">{v.plate}</span> · {v.model} · {v.type}
            </div>
          ))}
        </Section>
      )}

      {/* Activity counters */}
      <Section title="Atividade" testid="detail-section-activity">
        <KV k="Publicações" v={c.posts} />
        <KV k="Corridas como motorista" v={c.rides_as_driver} />
        <KV k="Corridas como passageiro" v={c.rides_as_rider} />
        <KV k="Anúncios" v={c.ads} />
        <KV k="Páginas criadas" v={c.pages} />
        <KV k="Denúncias contra o usuário" v={c.reports_against_user} />
        <KV k="Denúncias feitas pelo usuário" v={c.reports_filed_by_user} />
      </Section>

      {/* Recent transactions */}
      <Section title="Wallet (últimas transações R$)" testid="detail-section-tx-wallet">
        {(tx.wallet || []).length === 0 && <div className="text-xs text-muted-foreground py-2">Sem movimentações.</div>}
        {(tx.wallet || []).map((t) => (
          <div key={t.tx_id} className="flex items-center gap-2 text-xs py-1 border-b border-dashed border-border last:border-0">
            <span className="text-[10px] font-mono text-muted-foreground w-32 truncate">{t.type}</span>
            <span className={`font-semibold ${t.amount < 0 ? "text-destructive" : "text-emerald-600"} font-mono`}>{t.amount > 0 ? "+" : ""}{Number(t.amount || 0).toFixed(2)}</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{t.created_at ? new Date(t.created_at).toLocaleString("pt-BR") : ""}</span>
          </div>
        ))}
      </Section>

      <Section title="Moedas FUTURO PDV (últimas)" testid="detail-section-tx-coins">
        {(tx.coins || []).length === 0 && <div className="text-xs text-muted-foreground py-2">Sem eventos.</div>}
        {(tx.coins || []).map((t) => (
          <div key={t.tx_id} className="flex items-center gap-2 text-xs py-1 border-b border-dashed border-border last:border-0">
            <span className="text-[10px] font-mono text-muted-foreground w-36 truncate">{t.action}</span>
            <span className="font-semibold text-primary font-mono">+{t.amount}</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{t.created_at ? new Date(t.created_at).toLocaleString("pt-BR") : ""}</span>
          </div>
        ))}
      </Section>

      <Section title="Saques" testid="detail-section-withdrawals">
        {(tx.withdrawals || []).length === 0 && <div className="text-xs text-muted-foreground py-2">Nenhum saque.</div>}
        {(tx.withdrawals || []).map((w) => (
          <div key={w.withdrawal_id} className="flex items-center gap-2 text-xs py-1 border-b border-dashed border-border last:border-0">
            <span className="font-mono text-[10px] w-32 truncate">{w.withdrawal_id}</span>
            <span className="font-semibold font-mono">R$ {Number(w.amount || 0).toFixed(2)}</span>
            <Badge variant="outline" className="text-[10px]">{w.status}</Badge>
            <span className="text-[10px] text-muted-foreground ml-auto">{w.created_at ? new Date(w.created_at).toLocaleString("pt-BR") : ""}</span>
          </div>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-primary font-bold mb-2">{title}</div>
      <div className="space-y-0">{children}</div>
    </Card>
  );
}

// ---------- Investors admin tab ----------
function InvestorsTab() {
  const [plans, setPlans] = useState([]);
  const [interests, setInterests] = useState([]);
  const [form, setForm] = useState({ name: "", amount: 1000, roi_pct: 10, term_months: 12, description: "", active: true, featured: false });
  const [refPct, setRefPct] = useState(0);
  const [refPctSaving, setRefPctSaving] = useState(false);
  const [referrals, setReferrals] = useState([]);

  const load = async () => {
    const [p, i, s, r] = await Promise.all([
      axios.get(`${API}/admin/investments`),
      axios.get(`${API}/admin/investment-interests`),
      axios.get(`${API}/admin/settings`),
      axios.get(`${API}/admin/investor-referrals`),
    ]);
    setPlans(p.data);
    setInterests(i.data);
    setRefPct(Number(s.data?.investor_referral_commission_pct || 0));
    setReferrals(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const saveRefPct = async () => {
    setRefPctSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { investor_referral_commission_pct: Number(refPct) });
      toast.success("Comissão de indicação salva!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
    setRefPctSaving(false);
  };

  const payReferral = async (id) => {
    try {
      await axios.put(`${API}/admin/investor-referrals/${id}/pay`);
      toast.success("Comissão paga!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao pagar");
    }
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Informe um nome");
    try {
      await axios.post(`${API}/admin/investments`, {
        ...form,
        amount: parseFloat(form.amount) || 0,
        roi_pct: parseFloat(form.roi_pct) || 0,
        term_months: parseInt(form.term_months) || 12,
      });
      toast.success("Plano criado!");
      setForm({ name: "", amount: 1000, roi_pct: 10, term_months: 12, description: "", active: true, featured: false });
      load();
    } catch { toast.error("Erro"); }
  };

  const update = async (p) => {
    try {
      await axios.put(`${API}/admin/investments/${p.plan_id}`, p);
      toast.success("Plano atualizado");
      load();
    } catch { toast.error("Erro"); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/investments/${id}`);
    toast.success("Removido");
    load();
  };

  const setInterestStatus = async (id, status) => {
    await axios.put(`${API}/admin/investment-interests/${id}/status`, { status });
    load();
  };

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <Card className="p-5 h-fit">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Award className="w-5 h-5 text-primary-foreground" />
            </div>
            <h2 className="text-lg font-bold tracking-tight">Novo plano</h2>
          </div>
          <div className="space-y-3">
            <Input data-testid="admin-plan-name" placeholder="Nome (Starter, Growth, etc)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div>
              <Label className="text-xs">Aporte (R$)</Label>
              <Input data-testid="admin-plan-amount" type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Retorno (% ao ano)</Label>
              <Input data-testid="admin-plan-roi" type="number" min="0" step="0.5" value={form.roi_pct} onChange={(e) => setForm({ ...form, roi_pct: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Prazo (meses)</Label>
              <Input data-testid="admin-plan-term" type="number" min="1" value={form.term_months} onChange={(e) => setForm({ ...form, term_months: e.target.value })} />
            </div>
            <Textarea data-testid="admin-plan-description" placeholder="Descrição" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="flex items-center justify-between pt-1">
              <Label className="text-sm">Ativo</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Destaque</Label>
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            </div>
            <Button data-testid="admin-plan-create" onClick={create} className="w-full rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Criar plano
            </Button>
          </div>
        </Card>

        <div>
          <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">
            Planos ({plans.length})
          </h3>
          <div className="space-y-3">
            {plans.length === 0 && <Card className="p-8 text-center text-muted-foreground">Nenhum plano</Card>}
            {plans.map((p) => (
              <Card key={p.plan_id} className="p-4" data-testid={`plan-row-${p.plan_id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2 items-center">
                    <div>
                      <div className="text-xs text-muted-foreground">Nome</div>
                      <Input value={p.name} onChange={(e) => { p.name = e.target.value; setPlans([...plans]); }} className="h-8 text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Aporte R$</div>
                      <Input type="number" value={p.amount} onChange={(e) => { p.amount = parseFloat(e.target.value) || 0; setPlans([...plans]); }} className="h-8 text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">ROI %</div>
                      <Input type="number" step="0.5" value={p.roi_pct} onChange={(e) => { p.roi_pct = parseFloat(e.target.value) || 0; setPlans([...plans]); }} className="h-8 text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Meses</div>
                      <Input type="number" value={p.term_months} onChange={(e) => { p.term_months = parseInt(e.target.value) || 12; setPlans([...plans]); }} className="h-8 text-sm" />
                    </div>
                  </div>
                  <Switch checked={!!p.active} onCheckedChange={(v) => { p.active = v; update(p); }} />
                  <Button size="sm" onClick={() => update(p)} className="rounded-full">Salvar</Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.plan_id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">
          Interesses recebidos ({interests.length})
        </h3>
        <div className="space-y-3">
          {interests.length === 0 && <Card className="p-8 text-center text-muted-foreground">Nenhum interesse ainda</Card>}
          {interests.map((it) => (
            <Card key={it.interest_id} className="p-4" data-testid={`interest-${it.interest_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold">{it.contact_name}</span>
                    <Badge variant="secondary">{it.plan_name}</Badge>
                    <Badge className={it.status === "new" ? "bg-blue-500" : it.status === "contacted" ? "bg-orange-500" : "bg-green-600"}>{it.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {it.contact_email} · {it.contact_phone} · R$ {(it.plan_amount || 0).toLocaleString("pt-BR")}
                  </div>
                  {it.message && <p className="text-sm mt-1">{it.message}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setInterestStatus(it.interest_id, "contacted")}>Contatar</Button>
                  <Button size="sm" variant="outline" onClick={() => setInterestStatus(it.interest_id, "closed")}>Fechar</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Investor referral commission widget */}
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <Card className="p-5 h-fit border-emerald-500/40 bg-emerald-500/5" data-testid="investor-referral-widget">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
              <Users2 className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-bold tracking-tight">Comissão de indicação</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Percentual pago ao indicador sobre o valor do aporte de um investidor indicado. Ex: 3% de R$ 10.000 = R$ 300 depositados na carteira do indicador ao confirmar o pagamento.
          </p>
          <div className="space-y-3">
            <Label className="text-xs">Percentual (%)</Label>
            <div className="flex items-center gap-3">
              <Slider
                data-testid="investor-referral-slider"
                min={0} max={20} step={0.5}
                value={[Number(refPct) || 0]}
                onValueChange={(v) => setRefPct(v[0])}
                className="flex-1"
              />
              <Input
                data-testid="investor-referral-input"
                type="number"
                min="0" max="50" step="0.5"
                value={refPct}
                onChange={(e) => setRefPct(e.target.value)}
                className="w-20 text-center"
              />
              <span className="text-sm font-semibold">%</span>
            </div>
            <Button
              data-testid="investor-referral-save"
              onClick={saveRefPct}
              disabled={refPctSaving}
              className="w-full rounded-full"
            >
              {refPctSaving ? "Salvando..." : "Salvar comissão"}
            </Button>
          </div>
        </Card>

        <div>
          <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">
            Indicações registradas ({referrals.length})
          </h3>
          <div className="space-y-2">
            {referrals.length === 0 && (
              <Card className="p-8 text-center text-muted-foreground" data-testid="investor-referral-empty">
                Nenhuma indicação registrada ainda.
              </Card>
            )}
            {referrals.map((r) => (
              <Card key={r.referral_id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`investor-referral-${r.referral_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">
                    Indicador: {r.referrer_name || r.referrer_user_id}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Investidor: {r.investor_name || "—"} · Aporte R$ {(r.investment_amount || 0).toLocaleString("pt-BR")} · Comissão R$ {(r.commission_amount || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </div>
                  {r.paid_at && <div className="text-[10px] text-emerald-600 mt-0.5">Paga em {new Date(r.paid_at).toLocaleDateString("pt-BR")}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={r.status === "paid" ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"} data-testid={`investor-referral-status-${r.referral_id}`}>
                    {r.status === "paid" ? "Paga" : "Pendente"}
                  </Badge>
                  {r.status !== "paid" && (
                    <Button
                      size="sm"
                      onClick={() => payReferral(r.referral_id)}
                      data-testid={`investor-referral-pay-${r.referral_id}`}
                      className="rounded-full"
                    >
                      Pagar
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Work tab ----------
function WorkTab() {
  const [texts, setTexts] = useState({ heading: "", subheading: "", create_btn: "", empty_state: "", dialog_title: "", apply_btn: "", info_banner: "" });
  const [contractorFee, setContractorFee] = useState(0);
  const [workerFee, setWorkerFee] = useState(0);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/work/config`);
      setTexts({ heading: "", subheading: "", create_btn: "", empty_state: "", dialog_title: "", apply_btn: "", info_banner: "", ...(r.data.texts || {}) });
      setContractorFee(r.data.contractor_fee_pct || 0);
      setWorkerFee(r.data.worker_fee_pct || 0);
      setGoals(r.data.goals || []);
    } catch (err) {
      console.error("[Admin] WorkTab load failed", err);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        work_texts: texts,
        work_contractor_fee_pct: parseFloat(contractorFee) || 0,
        work_worker_fee_pct: parseFloat(workerFee) || 0,
        work_goals: goals.map(g => ({
          name: String(g.name || "").trim() || "Meta",
          jobs_target: parseInt(g.jobs_target) || 0,
          bonus_brl: parseFloat(g.bonus_brl) || 0,
        })),
      });
      toast.success("Configurações de trabalhos salvas!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setLoading(false);
  };

  const addGoal = () => setGoals([...goals, { name: "Nova meta", jobs_target: 5, bonus_brl: 20 }]);
  const removeGoal = (i) => setGoals(goals.filter((_, idx) => idx !== i));
  const updateGoal = (i, field, val) => setGoals(goals.map((g, idx) => idx === i ? { ...g, [field]: val } : g));

  const textFields = [
    { key: "heading", label: "Título principal", placeholder: "Ex: Trabalhos" },
    { key: "subheading", label: "Subtítulo (mensagem principal)", placeholder: "Ex: Encontre ou publique trabalhos" },
    { key: "create_btn", label: "Texto do botão de publicar", placeholder: "Ex: Publicar trabalho" },
    { key: "dialog_title", label: "Título do modal de publicação", placeholder: "Ex: Publicar um trabalho" },
    { key: "empty_state", label: "Mensagem quando não há trabalhos", placeholder: "Ex: Nenhum trabalho ainda. Seja o primeiro!" },
    { key: "apply_btn", label: "Texto do botão de candidatura", placeholder: "Ex: Candidatar-se" },
    { key: "info_banner", label: "Banner informativo (opcional)", placeholder: "Ex: Novas oportunidades toda semana!" },
  ];

  return (
    <div className="space-y-6 max-w-5xl">
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Textos exibidos na área de Trabalho</h2>
            <p className="text-xs text-muted-foreground">Edite as mensagens que aparecem para os usuários</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {textFields.map(f => (
            <div key={f.key}>
              <Label className="text-xs">{f.label}</Label>
              <Input
                data-testid={`work-text-${f.key}`}
                placeholder={f.placeholder}
                value={texts[f.key] || ""}
                onChange={(e) => setTexts({ ...texts, [f.key]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-600 flex items-center justify-center">
            <Percent className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Taxas da área de trabalho</h2>
            <p className="text-xs text-muted-foreground">Percentual cobrado pela plataforma</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-baseline justify-between">
              <Label className="text-sm">Taxa sobre contratantes (quem publica)</Label>
              <div className="text-2xl font-bold text-orange-600" data-testid="work-contractor-fee-value">{Number(contractorFee).toFixed(1)}%</div>
            </div>
            <Slider data-testid="work-contractor-fee-slider" value={[Number(contractorFee)]} onValueChange={(v) => setContractorFee(v[0])} min={0} max={50} step={0.5} />
            <p className="text-xs text-muted-foreground mt-1">Adicionada ao valor pago pelo contratante ao publicar um trabalho.</p>
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <Label className="text-sm">Taxa sobre trabalhadores (quem realiza)</Label>
              <div className="text-2xl font-bold text-orange-600" data-testid="work-worker-fee-value">{Number(workerFee).toFixed(1)}%</div>
            </div>
            <Slider data-testid="work-worker-fee-slider" value={[Number(workerFee)]} onValueChange={(v) => setWorkerFee(v[0])} min={0} max={50} step={0.5} />
            <p className="text-xs text-muted-foreground mt-1">Descontada do pagamento recebido pelo trabalhador ao concluir um trabalho.</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
              <Award className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">Metas & bônus de ganhos</h2>
              <p className="text-xs text-muted-foreground">Quantos trabalhos concluídos rendem qual bônus (R$)</p>
            </div>
          </div>
          <Button size="sm" data-testid="work-add-goal" onClick={addGoal} className="rounded-full gap-1"><Plus className="w-4 h-4" />Nova meta</Button>
        </div>
        <div className="space-y-2">
          {goals.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma meta configurada</p>}
          {goals.map((g, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_120px_40px] gap-2 items-end p-3 rounded-lg border border-border" data-testid={`work-goal-row-${i}`}>
              <div>
                <Label className="text-[10px]">Nome</Label>
                <Input value={g.name} onChange={(e) => updateGoal(i, "name", e.target.value)} data-testid={`work-goal-name-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Trabalhos p/ atingir</Label>
                <Input type="number" min="1" value={g.jobs_target} onChange={(e) => updateGoal(i, "jobs_target", e.target.value)} data-testid={`work-goal-target-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Bônus (R$)</Label>
                <Input type="number" min="0" step="0.5" value={g.bonus_brl} onChange={(e) => updateGoal(i, "bonus_brl", e.target.value)} data-testid={`work-goal-bonus-${i}`} />
              </div>
              <Button size="icon" variant="ghost" onClick={() => removeGoal(i)} data-testid={`work-goal-remove-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Button data-testid="work-save-btn" onClick={save} disabled={loading} className="rounded-full">
        {loading ? "Salvando..." : "Salvar configurações de trabalhos"}
      </Button>
    </div>
  );
}

// ---------- Tiers tab (Bronze/Prata/Ouro/Diamante) ----------
function TiersTab() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const defaults = [
    { name: "Bronze", activity_target: 5, coin_bonus: 50, color: "#cd7f32", perks: "Selo Bronze" },
    { name: "Prata", activity_target: 25, coin_bonus: 200, color: "#c0c0c0", perks: "Selo Prata + destaque em buscas" },
    { name: "Ouro", activity_target: 75, coin_bonus: 600, color: "#facc15", perks: "Selo Ouro + prioridade + 5% desconto" },
    { name: "Diamante", activity_target: 200, coin_bonus: 2000, color: "#67e8f9", perks: "Selo Diamante + concierge + 10% desconto" },
  ];

  useEffect(() => {
    axios.get(`${API}/tiers/config`).then((r) => {
      setTiers((r.data.tiers && r.data.tiers.length > 0) ? r.data.tiers : defaults);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        user_tiers: tiers.map(t => ({
          name: String(t.name || "").trim() || "Categoria",
          activity_target: parseInt(t.activity_target) || 0,
          coin_bonus: parseInt(t.coin_bonus) || 0,
          color: String(t.color || "#888"),
          perks: String(t.perks || ""),
        })),
      });
      toast.success("Categorias salvas!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setLoading(false);
  };

  const update = (i, field, val) => setTiers(tiers.map((t, idx) => idx === i ? { ...t, [field]: val } : t));

  return (
    <div className="space-y-6 max-w-5xl">
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Categorias de qualificação</h2>
            <p className="text-xs text-muted-foreground">Atividade = <strong>trabalhos concluídos</strong> + <strong>viagens</strong>. Configure a meta de atividade e o bônus em <span className="font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">FUTURO PDV</span> por categoria.</p>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {tiers.map((t, i) => (
          <Card key={i} className="p-5" data-testid={`tier-card-${i}`} style={{ borderColor: (t.color || "#888") + "66" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: t.color || "#888" }}>
                <Award className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <Input data-testid={`tier-name-${i}`} value={t.name || ""} onChange={(e) => update(i, "name", e.target.value)} placeholder="Nome" className="font-bold" />
              </div>
              <input
                data-testid={`tier-color-${i}`}
                type="color"
                value={t.color || "#888"}
                onChange={(e) => update(i, "color", e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border border-border bg-transparent"
                title="Cor da categoria"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs">Atividade necessária</Label>
                <Input data-testid={`tier-target-${i}`} type="number" min="0" value={t.activity_target} onChange={(e) => update(i, "activity_target", e.target.value)} />
                <p className="text-[10px] text-muted-foreground mt-1">trabalhos + viagens</p>
              </div>
              <div>
                <Label className="text-xs">Bônus FUTURO PDV</Label>
                <Input data-testid={`tier-bonus-${i}`} type="number" min="0" value={t.coin_bonus} onChange={(e) => update(i, "coin_bonus", e.target.value)} />
                <p className="text-[10px] text-muted-foreground mt-1">creditados ao resgatar</p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Benefícios e reconhecimento</Label>
              <Textarea data-testid={`tier-perks-${i}`} rows={2} value={t.perks || ""} onChange={(e) => update(i, "perks", e.target.value)} placeholder="Ex: Prioridade em corridas, desconto de 5%..." />
            </div>
          </Card>
        ))}
      </div>

      <Button data-testid="tiers-save-btn" onClick={save} disabled={loading} className="rounded-full">
        {loading ? "Salvando..." : "Salvar categorias"}
      </Button>
    </div>
  );
}

// ---------- Admins tab (super-admin only) ----------
function AdminsTab() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [step, setStep] = useState("form"); // form | codes
  const [invite, setInvite] = useState(null);
  const [emailCode, setEmailCode] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/super-admin/admins`);
      setAdmins(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Sem acesso"); }
  };
  useEffect(() => { load(); }, []);

  const createInvite = async () => {
    if (!form.name || !form.email || !form.phone) return toast.error("Preencha nome, e-mail e celular");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/super-admin/invites`, form);
      setInvite(r.data);
      setStep("codes");
      toast.success("Códigos enviados. Verifique seu e-mail e SMS.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  const confirmInvite = async () => {
    if (!emailCode || !smsCode) return toast.error("Digite os dois códigos");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/super-admin/invites/confirm`, {
        invite_id: invite.invite_id, email_code: emailCode.trim(), sms_code: smsCode.trim(),
      });
      setResult(r.data);
      toast.success("Novo administrador criado com sucesso!");
      setForm({ name: "", email: "", phone: "" });
      setEmailCode(""); setSmsCode(""); setInvite(null);
      setStep("form");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  const removeAdmin = async (uid) => {
    if (!window.confirm("Remover privilégio de administrador?")) return;
    try {
      await axios.delete(`${API}/super-admin/admins/${uid}`);
      toast.success("Administrador removido");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <Card className="p-6 border-cyan-500/40 bg-cyan-500/5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-cyan-600 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Adicionar novo administrador</h2>
            <p className="text-xs text-muted-foreground">Exige confirmação por <strong>e-mail</strong> + <strong>SMS</strong> do administrador principal (você) para garantir a segurança da plataforma.</p>
          </div>
        </div>

        {step === "form" && (
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Nome</Label>
              <Input data-testid="new-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">E-mail</Label>
              <Input data-testid="new-admin-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Celular (com DDD)</Label>
              <Input data-testid="new-admin-phone" placeholder="+55 11 9..." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="md:col-span-3">
              <Button data-testid="new-admin-send-codes" onClick={createInvite} disabled={busy} className="rounded-full">
                {busy ? "Enviando..." : "Enviar códigos de confirmação"}
              </Button>
            </div>
          </div>
        )}

        {step === "codes" && invite && (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-muted/50 text-xs">
              Enviamos os códigos para <strong>{invite.email_sent_to}</strong> (e-mail) e <strong>{invite.sms_sent_to}</strong> (SMS). Expiram em 15 minutos.
            </div>
            {(invite.email_mocked || invite.sms_mocked) && (
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/40 text-xs space-y-1">
                <div><strong>Modo dev (sem chaves de e-mail/SMS):</strong></div>
                {invite.email_mocked && <div>E-mail: <code className="font-mono">{invite.dev_email_code}</code></div>}
                {invite.sms_mocked && <div>SMS: <code className="font-mono">{invite.dev_sms_code}</code></div>}
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Código do e-mail</Label>
                <Input data-testid="new-admin-email-code" inputMode="numeric" maxLength={6} value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="text-center text-xl tracking-[0.4em] font-mono" />
              </div>
              <div>
                <Label className="text-xs">Código do SMS</Label>
                <Input data-testid="new-admin-sms-code" inputMode="numeric" maxLength={6} value={smsCode} onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="text-center text-xl tracking-[0.4em] font-mono" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setStep("form"); setInvite(null); }} className="rounded-full">Voltar</Button>
              <Button data-testid="new-admin-confirm" onClick={confirmInvite} disabled={busy} className="flex-1 rounded-full">
                {busy ? "Confirmando..." : "Confirmar e criar administrador"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {result?.dev_tmp_password && (
        <Card className="p-4 border-orange-500/40 bg-orange-500/5 text-sm">
          <strong>Modo dev:</strong> senha temporária do novo admin <strong>{result.new_admin_email}</strong>: <code className="font-mono">{result.dev_tmp_password}</code>
        </Card>
      )}

      <Card className="p-6">
        <h2 className="text-lg font-bold tracking-tight mb-4">Administradores cadastrados</h2>
        <div className="space-y-2">
          {admins.map(a => (
            <div key={a.user_id} className="p-3 rounded-lg border border-border flex items-center justify-between gap-3" data-testid={`admin-row-${a.user_id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm">{a.name}</span>
                  {a.is_super_admin && <Badge className="bg-cyan-600 text-[10px]">Principal</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">{a.email} · {a.phone || "sem celular"}</div>
              </div>
              {!a.is_super_admin && (
                <Button size="sm" variant="ghost" onClick={() => removeAdmin(a.user_id)} data-testid={`admin-remove-${a.user_id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ---------- Jobs Management tab (freelancers + companies + subscription plans) ----------
function JobsMgmtTab() {
  const [freelancers, setFreelancers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [autonomos, setAutonomos] = useState([]);
  const [autoPlans, setAutoPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [f, c, p, a, ap] = await Promise.all([
        axios.get(`${API}/admin/freelancers`),
        axios.get(`${API}/admin/companies`),
        axios.get(`${API}/job-plans`),
        axios.get(`${API}/admin/autonomos`),
        axios.get(`${API}/autonomo-plans`),
      ]);
      setFreelancers(f.data);
      setCompanies(c.data);
      setPlans(p.data);
      setAutonomos(a.data);
      setAutoPlans(ap.data);
    } catch (err) {
      console.error("[Admin] jobs-mgmt load failed", err);
    }
  };
  useEffect(() => { load(); }, []);

  const updatePlan = (i, field, val) => setPlans(plans.map((p, idx) => idx === i ? { ...p, [field]: val } : p));
  const addPlan = () => setPlans([...plans, { plan_id: `plan_${Date.now()}`, name: "Novo plano", days: 30, base_price_brl: 100, discount_pct: 0, features: [] }]);
  const removePlan = (i) => setPlans(plans.filter((_, idx) => idx !== i));

  const savePlans = async () => {
    setLoading(true);
    try {
      const payload = plans.map(p => ({
        plan_id: p.plan_id, name: p.name,
        days: parseInt(p.days) || 1,
        base_price_brl: parseFloat(p.base_price_brl) || 0,
        discount_pct: parseFloat(p.discount_pct) || 0,
        features: Array.isArray(p.features) ? p.features : String(p.features || "").split(",").map(s => s.trim()).filter(Boolean),
      }));
      await axios.put(`${API}/admin/job-plans`, { plans: payload });
      toast.success("Planos salvos!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setLoading(false);
  };

  const saveAutoPlans = async () => {
    setLoading(true);
    try {
      const payload = autoPlans.map(p => ({
        plan_id: p.plan_id, name: p.name,
        days: parseInt(p.days) || 1,
        base_price_brl: parseFloat(p.base_price_brl) || 0,
        discount_pct: parseFloat(p.discount_pct) || 0,
        features: Array.isArray(p.features) ? p.features : String(p.features || "").split(",").map(s => s.trim()).filter(Boolean),
      }));
      await axios.put(`${API}/admin/autonomo-plans`, { plans: payload });
      toast.success("Planos de autônomos salvos!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setLoading(false);
  };

  const updateAutoPlan = (i, field, val) => setAutoPlans(autoPlans.map((p, idx) => idx === i ? { ...p, [field]: val } : p));
  const addAutoPlan = () => setAutoPlans([...autoPlans, { plan_id: `ap_${Date.now()}`, name: "Novo plano", days: 30, base_price_brl: 50, discount_pct: 0, features: [] }]);
  const removeAutoPlan = (i) => setAutoPlans(autoPlans.filter((_, idx) => idx !== i));

  const deleteAutonomo = async (uid) => {
    if (!window.confirm("Remover perfil de autônomo?")) return;
    await axios.delete(`${API}/admin/autonomos/${uid}`);
    toast.success("Autônomo removido");
    load();
  };

  const deleteFreelancer = async (uid) => {
    if (!window.confirm("Remover perfil de freelancer?")) return;
    await axios.delete(`${API}/admin/freelancers/${uid}`);
    toast.success("Perfil removido");
    load();
  };
  const deleteCompany = async (uid) => {
    if (!window.confirm("Remover cadastro de empresa?")) return;
    await axios.delete(`${API}/admin/companies/${uid}`);
    toast.success("Empresa removida");
    load();
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Planos de anúncio de vagas</h2>
            <p className="text-xs text-muted-foreground">Configure valores e descontos progressivos</p>
          </div>
          <Button size="sm" data-testid="add-job-plan" onClick={addPlan} className="rounded-full gap-1"><Plus className="w-4 h-4" />Novo plano</Button>
        </div>
        <div className="space-y-3">
          {plans.map((p, i) => (
            <div key={i} className="grid grid-cols-[1fr_100px_120px_100px_2fr_40px] gap-2 items-end p-3 rounded-lg border border-border" data-testid={`job-plan-row-${i}`}>
              <div>
                <Label className="text-[10px]">Nome</Label>
                <Input value={p.name} onChange={(e) => updatePlan(i, "name", e.target.value)} data-testid={`plan-name-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Dias</Label>
                <Input type="number" min="1" value={p.days} onChange={(e) => updatePlan(i, "days", e.target.value)} data-testid={`plan-days-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Preço base (R$)</Label>
                <Input type="number" min="0" step="0.5" value={p.base_price_brl} onChange={(e) => updatePlan(i, "base_price_brl", e.target.value)} data-testid={`plan-price-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Desconto %</Label>
                <Input type="number" min="0" max="99" step="1" value={p.discount_pct} onChange={(e) => updatePlan(i, "discount_pct", e.target.value)} data-testid={`plan-discount-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Features (vírgula)</Label>
                <Input value={Array.isArray(p.features) ? p.features.join(", ") : (p.features || "")} onChange={(e) => updatePlan(i, "features", e.target.value)} data-testid={`plan-features-${i}`} />
              </div>
              <Button size="icon" variant="ghost" onClick={() => removePlan(i)} data-testid={`plan-remove-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
        <Button className="mt-4 rounded-full" onClick={savePlans} disabled={loading} data-testid="save-job-plans">
          {loading ? "Salvando..." : "Salvar planos"}
        </Button>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-lg font-bold tracking-tight mb-3">Freelancers cadastrados ({freelancers.length})</h2>
          {freelancers.length === 0 && <p className="text-sm text-muted-foreground">Nenhum freelancer</p>}
          <div className="space-y-2 max-h-[480px] overflow-y-auto">
            {freelancers.map(f => (
              <div key={f.user_id} className="p-3 rounded-lg border border-border" data-testid={`admin-fl-${f.user_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-sm">{f.name}</div>
                    <div className="text-xs text-muted-foreground">{f.email} · {f.city || "sem cidade"} · R$ {f.hourly_rate_brl || 0}/h</div>
                    <div className="text-xs text-muted-foreground">Skills: {(f.skills || []).slice(0,5).join(", ") || "-"}</div>
                    <div className="text-xs text-muted-foreground">Convites recebidos: {f.invites_received}</div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => deleteFreelancer(f.user_id)} data-testid={`admin-fl-delete-${f.user_id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-bold tracking-tight mb-3">Empresas cadastradas ({companies.length})</h2>
          {companies.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma empresa</p>}
          <div className="space-y-2 max-h-[480px] overflow-y-auto">
            {companies.map(c => (
              <div key={c.user_id} className="p-3 rounded-lg border border-border" data-testid={`admin-co-${c.user_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-bold text-sm">{c.company_name}</div>
                      {!c.name_visible && <Badge variant="outline" className="text-[10px]">Confidencial</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">{c.email} · {c.owner_name} · {c.cnpj || "sem CNPJ"}</div>
                    <div className="text-xs text-muted-foreground">Vagas: {c.jobs_count} · Plano: <strong>{c.active_subscription?.plan_name || "sem plano ativo"}</strong></div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => deleteCompany(c.user_id)} data-testid={`admin-co-delete-${c.user_id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Autonomos plans + list */}
      <Card className="p-6" data-testid="admin-autonomo-plans">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Planos de autônomos</h2>
            <p className="text-xs text-muted-foreground">Assinaturas que publicam a página de divulgação do profissional autônomo.</p>
          </div>
          <Button size="sm" data-testid="add-auto-plan" onClick={addAutoPlan} className="rounded-full gap-1"><Plus className="w-4 h-4" />Novo plano</Button>
        </div>
        <div className="space-y-3">
          {autoPlans.map((p, i) => (
            <div key={i} className="grid grid-cols-[1fr_100px_120px_100px_2fr_40px] gap-2 items-end p-3 rounded-lg border border-border" data-testid={`admin-auto-plan-row-${i}`}>
              <div>
                <Label className="text-[10px]">Nome</Label>
                <Input value={p.name} onChange={(e) => updateAutoPlan(i, "name", e.target.value)} data-testid={`auto-plan-name-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Dias</Label>
                <Input type="number" min="1" value={p.days} onChange={(e) => updateAutoPlan(i, "days", e.target.value)} data-testid={`auto-plan-days-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Preço base (R$)</Label>
                <Input type="number" min="0" step="0.5" value={p.base_price_brl} onChange={(e) => updateAutoPlan(i, "base_price_brl", e.target.value)} data-testid={`auto-plan-price-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Desconto %</Label>
                <Input type="number" min="0" max="99" step="1" value={p.discount_pct} onChange={(e) => updateAutoPlan(i, "discount_pct", e.target.value)} data-testid={`auto-plan-discount-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">Features (vírgula)</Label>
                <Input value={Array.isArray(p.features) ? p.features.join(", ") : (p.features || "")} onChange={(e) => updateAutoPlan(i, "features", e.target.value)} data-testid={`auto-plan-features-${i}`} />
              </div>
              <Button size="icon" variant="ghost" onClick={() => removeAutoPlan(i)} data-testid={`auto-plan-remove-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
        <Button className="mt-4 rounded-full" onClick={saveAutoPlans} disabled={loading} data-testid="save-auto-plans">
          {loading ? "Salvando..." : "Salvar planos de autônomos"}
        </Button>
      </Card>

      <Card className="p-6">
        <h2 className="text-lg font-bold tracking-tight mb-3">Autônomos cadastrados ({autonomos.length})</h2>
        {autonomos.length === 0 && <p className="text-sm text-muted-foreground">Nenhum autônomo</p>}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {autonomos.map(a => (
            <div key={a.user_id} className="p-3 rounded-lg border border-border" data-testid={`admin-auto-${a.user_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-sm">{a.name || a.profession}</div>
                    <Badge variant="outline" className="text-[10px]">{a.profession}</Badge>
                    {a.quote_mode === "paid" ? (
                      <Badge className="text-[10px] bg-orange-500">R$ {Number(a.quote_price_brl || 0).toFixed(2)}</Badge>
                    ) : (
                      <Badge className="text-[10px] bg-green-500">Grátis</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{a.email} · {a.city || "sem cidade"}</div>
                  <div className="text-xs text-muted-foreground">Plano: <strong>{a.active_subscription?.plan_name || "sem plano ativo"}</strong></div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => deleteAutonomo(a.user_id)} data-testid={`admin-auto-delete-${a.user_id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}


// ---------- Site texts + support email ----------
function SiteTextsTab() {
  const [supportEmail, setSupportEmail] = useState("");
  const [supportWhatsapp, setSupportWhatsapp] = useState("");
  const [texts, setTexts] = useState({});
  const [loading, setLoading] = useState(false);
  const sections = [
    { key: "feed", label: "Feed social", fields: [
      { key: "empty_state", label: "Mensagem quando não há posts", placeholder: "Ex: Seja o primeiro a publicar!" },
      { key: "composer_placeholder", label: "Placeholder do composer", placeholder: "Ex: No que você está pensando?" },
    ]},
    { key: "mobility", label: "Mobilidade", fields: [
      { key: "heading", label: "Título", placeholder: "Ex: Peça uma corrida" },
      { key: "subheading", label: "Subtítulo", placeholder: "" },
    ]},
    { key: "chat", label: "Futuro Chat", fields: [
      { key: "empty_state", label: "Sem conversas", placeholder: "Ex: Comece uma conversa!" },
    ]},
    { key: "wallet", label: "Carteira", fields: [
      { key: "heading", label: "Título", placeholder: "Ex: Minha carteira" },
      { key: "info_banner", label: "Aviso opcional", placeholder: "" },
    ]},
    { key: "investors", label: "Investidores", fields: [
      { key: "heading", label: "Título", placeholder: "Ex: Torne-se sócio do FUTURO PDV" },
      { key: "cta", label: "CTA", placeholder: "Ex: Ver planos" },
    ]},
    { key: "ads", label: "Anúncios", fields: [
      { key: "heading", label: "Título", placeholder: "Ex: Impulsione seu negócio" },
    ]},
  ];

  const load = async () => {
    const [pub, cfg] = await Promise.all([
      axios.get(`${API}/site-texts`),
      axios.get(`${API}/admin/settings`),
    ]);
    setSupportEmail(pub.data.support_email || cfg.data.support_email || "");
    setSupportWhatsapp(pub.data.support_whatsapp || cfg.data.support_whatsapp || "");
    setTexts(pub.data.site_texts || {});
  };
  useEffect(() => { load(); }, []);

  const updateText = (section, key, val) => {
    setTexts({ ...texts, [section]: { ...(texts[section] || {}), [key]: val } });
  };

  const save = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        site_texts: texts,
        support_email: supportEmail || null,
        support_whatsapp: supportWhatsapp,
      });
      toast.success("Textos e contatos de suporte salvos!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setLoading(false);
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <Card className="p-6 border-cyan-500/40 bg-cyan-500/5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-cyan-600 flex items-center justify-center"><Send className="w-5 h-5 text-white" /></div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Contatos de suporte</h2>
            <p className="text-xs text-muted-foreground">Todos os usuários verão "Ajuda / Suporte" (e-mail) e "Falar no WhatsApp" no menu do avatar.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">E-mail de suporte</Label>
            <Input data-testid="admin-support-email" type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} placeholder="suporte@pdvfuturo.com" />
          </div>
          <div>
            <Label className="text-xs">WhatsApp de suporte (DDI+DDD+número)</Label>
            <Input data-testid="admin-support-whatsapp" inputMode="numeric" value={supportWhatsapp} onChange={(e) => setSupportWhatsapp(e.target.value)} placeholder="5511999999999" />
            <p className="text-[11px] text-muted-foreground mt-1">Aparece como "Falar no WhatsApp" no menu do usuário. Deixe vazio para ocultar.</p>
          </div>
        </div>
      </Card>

      {sections.map(sec => (
        <Card key={sec.key} className="p-6" data-testid={`site-section-${sec.key}`}>
          <h3 className="text-md font-bold tracking-tight mb-3">Textos: {sec.label}</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {sec.fields.map(f => (
              <div key={f.key}>
                <Label className="text-xs">{f.label}</Label>
                <Input
                  data-testid={`site-text-${sec.key}-${f.key}`}
                  placeholder={f.placeholder}
                  value={texts[sec.key]?.[f.key] || ""}
                  onChange={(e) => updateText(sec.key, f.key, e.target.value)}
                />
              </div>
            ))}
          </div>
        </Card>
      ))}

      <Button data-testid="save-site-texts" onClick={save} disabled={loading} className="rounded-full">
        {loading ? "Salvando..." : "Salvar textos e suporte"}
      </Button>
    </div>
  );
}


// ---------- Sounds tab (feature 34) ----------
function SoundsTab() {
  const [form, setForm] = useState({
    sound_ride_request_url: "",
    sound_freelancer_invite_url: "",
    sound_freelancer_same_day_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/settings`);
      setForm({
        sound_ride_request_url: r.data.sound_ride_request_url || "",
        sound_freelancer_invite_url: r.data.sound_freelancer_invite_url || "",
        sound_freelancer_same_day_url: r.data.sound_freelancer_same_day_url || "",
      });
    } catch (e) { toast.error("Erro ao carregar sons"); }
  };
  useEffect(() => { load(); }, []);

  const uploadFile = async (field, file) => {
    if (!file) return;
    setUploading({ ...uploading, [field]: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Store relative path — same-origin resolution keeps the audio URL valid across domains.
      const url = r.data.url;
      setForm({ ...form, [field]: url });
      toast.success("Áudio enviado! Não esqueça de salvar.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no upload");
    }
    setUploading({ ...uploading, [field]: false });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, form);
      toast.success("Sons salvos!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  const preview = (url) => {
    if (!url) { toast.info("Nenhum áudio configurado"); return; }
    try { new Audio(url).play(); } catch { toast.error("Não foi possível reproduzir"); }
  };

  const fields = [
    { key: "sound_ride_request_url", label: "Nova corrida (motorista)", desc: "Toca quando o motorista recebe uma nova corrida no aplicativo." },
    { key: "sound_freelancer_invite_url", label: "Convite de freelancer", desc: "Toca quando o freelancer recebe um novo convite/trabalho." },
    { key: "sound_freelancer_same_day_url", label: "Freelancer chegou no dia (empresa)", desc: "Alerta para a empresa quando o freelancer marca 'estou a caminho' no dia." },
  ];

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-sounds-panel">
      <Card className="p-6 border-purple-500/40 bg-purple-500/5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center"><Volume2 className="w-5 h-5 text-white" /></div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Sons de notificação</h2>
            <p className="text-xs text-muted-foreground">Faça upload ou informe URLs .mp3/.wav para tocar em eventos importantes.</p>
          </div>
        </div>
      </Card>
      {fields.map((f) => (
        <Card key={f.key} className="p-4 space-y-2" data-testid={`sound-card-${f.key}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-sm font-semibold">{f.label}</div>
              <div className="text-xs text-muted-foreground">{f.desc}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => preview(form[f.key])} data-testid={`sound-preview-${f.key}`} className="rounded-full">
              <Volume2 className="w-4 h-4 mr-1" /> Ouvir
            </Button>
          </div>
          <div className="flex gap-2">
            <Input
              data-testid={`sound-url-${f.key}`}
              placeholder="https://... (URL do áudio)"
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            />
            <label className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-border text-xs cursor-pointer hover:bg-muted whitespace-nowrap">
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => uploadFile(f.key, e.target.files?.[0])}
                className="hidden"
                data-testid={`sound-upload-${f.key}`}
              />
              {uploading[f.key] ? "Enviando..." : "Upload MP3"}
            </label>
          </div>
        </Card>
      ))}
      <Button data-testid="save-sounds" onClick={save} disabled={saving} className="rounded-full">
        {saving ? "Salvando..." : "Salvar sons"}
      </Button>
    </div>
  );
}

// ---------- Driver Docs tab (feature 35) ----------
function DriverDocsTab() {
  const [alerts, setAlerts] = useState([]);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("alerts"); // "alerts" | "all"
  const [editing, setEditing] = useState(null); // { user_id, docs: {...} }
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(null); // { user_id, key }
  const [ocrResult, setOcrResult] = useState(null); // { doc_type, extracted, raw, key }

  const loadAlerts = async () => {
    try {
      const r = await axios.get(`${API}/admin/driver-docs`);
      setAlerts(r.data || []);
    } catch (e) { toast.error("Erro ao carregar"); }
  };
  const loadAll = async (search = "") => {
    try {
      const r = await axios.get(`${API}/admin/driver-docs/all`, { params: search ? { q: search } : {} });
      setAll(r.data || []);
    } catch (e) { toast.error("Erro ao carregar motoristas"); }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAlerts(), loadAll()]).finally(() => setLoading(false));
  }, []);

  const badgeFor = (status) => {
    if (status === "expired") return <Badge className="bg-red-500 text-white text-[10px]">Vencido</Badge>;
    if (status === "expiring_soon") return <Badge className="bg-orange-500 text-white text-[10px]">Vence em breve</Badge>;
    if (status === "missing") return <Badge variant="outline" className="text-[10px]">Sem informação</Badge>;
    return <Badge className="bg-emerald-500 text-white text-[10px]">Em dia</Badge>;
  };

  const startEdit = (d) => {
    setEditing({
      user_id: d.user_id,
      name: d.name,
      docs: { ...d.docs },
    });
  };

  const saveDocs = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/driver-docs/${editing.user_id}`, editing.docs);
      toast.success("Documentos atualizados!");
      setEditing(null);
      loadAll(q);
      loadAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const clearDoc = async (userId, docKey) => {
    if (!window.confirm("Excluir esta foto do motorista?")) return;
    try {
      await axios.delete(`${API}/admin/driver-docs/${userId}/${docKey}`);
      toast.success("Documento removido");
      if (editing && editing.user_id === userId) {
        setEditing({ ...editing, docs: { ...editing.docs, [docKey]: "" } });
      }
      loadAll(q);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const DOC_KEY_TO_TYPE = {
    cnh_photo_url: "cnh",
    identity_photo_url: "identity",
    vehicle_doc_photo_url: "vehicle_doc",
  };

  const runOcr = async (userId, docKey) => {
    const url = editing?.docs?.[docKey];
    if (!url) { toast.error("Envie a foto primeiro"); return; }
    if (url.toLowerCase().endsWith(".pdf") || url.toLowerCase().includes(".pdf?")) {
      toast.error("OCR não suporta PDF. Envie uma foto JPG/PNG.");
      return;
    }
    const docType = DOC_KEY_TO_TYPE[docKey];
    if (!docType) { toast.error("OCR indisponível para este campo"); return; }
    setOcrLoading({ user_id: userId, key: docKey });
    try {
      const r = await axios.post(`${API}/admin/driver-docs/${userId}/ocr`, {
        image_url: url,
        doc_type: docType,
      });
      setOcrResult({ doc_type: docType, extracted: r.data.extracted, raw: r.data.raw, key: docKey });
      if (!r.data.extracted) toast.warning("OCR concluído, mas não foi possível estruturar todos os campos.");
      else toast.success("OCR concluído!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no OCR");
    }
    setOcrLoading(null);
  };

  const DOC_FIELDS = [
    { key: "driver_photo_url", label: "Foto do motorista", accept: "image/*" },
    { key: "vehicle_photo_url", label: "Foto do veículo", accept: "image/*" },
    { key: "cnh_photo_url", label: "Foto/PDF da CNH", accept: "image/*,application/pdf" },
    { key: "identity_photo_url", label: "Documento de identidade", accept: "image/*,application/pdf" },
    { key: "vehicle_doc_photo_url", label: "Documento do veículo (CRLV)", accept: "image/*,application/pdf" },
  ];
  const isPdfUrl = (u) => !!u && (String(u).toLowerCase().endsWith(".pdf") || String(u).toLowerCase().includes(".pdf?"));

  return (
    <div className="space-y-4 max-w-5xl" data-testid="admin-driver-docs-panel">
      <Card className="p-6 border-red-500/40 bg-red-500/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-white" /></div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Documentos de motoristas</h2>
            <p className="text-xs text-muted-foreground">Visualize, adicione, substitua ou exclua as fotos dos documentos de qualquer motorista.</p>
          </div>
        </div>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant={view === "alerts" ? "default" : "outline"}
          onClick={() => setView("alerts")}
          data-testid="admin-driver-docs-view-alerts"
          className="rounded-full"
        >
          Alertas ({alerts.length})
        </Button>
        <Button
          size="sm"
          variant={view === "all" ? "default" : "outline"}
          onClick={() => setView("all")}
          data-testid="admin-driver-docs-view-all"
          className="rounded-full"
        >
          Todos motoristas ({all.length})
        </Button>
        {view === "all" && (
          <div className="flex items-center gap-2 ml-auto">
            <Input
              placeholder="Buscar por nome, e-mail ou placa"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadAll(q)}
              data-testid="admin-driver-docs-search"
              className="w-64"
            />
            <Button size="sm" variant="outline" onClick={() => loadAll(q)} data-testid="admin-driver-docs-search-btn">Buscar</Button>
          </div>
        )}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Carregando...</div>}

      {!loading && view === "alerts" && (
        <div className="space-y-3">
          {alerts.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground" data-testid="driver-docs-empty">
              Nenhum motorista com documentos pendentes. Tudo em dia!
            </Card>
          )}
          {alerts.map((d) => (
            <Card key={d.user_id} className="p-4" data-testid={`driver-doc-${d.user_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold">{d.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.email} · {d.phone || "sem telefone"} · {[d.vehicle_brand, d.vehicle_model, d.vehicle_plate].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-2 md:items-center text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">CNH:</span> {badgeFor(d.cnh?.status)}
                    {d.cnh?.days_left !== null && d.cnh?.days_left !== undefined && (
                      <span className="text-muted-foreground">({d.cnh.days_left}d)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Veículo:</span> {badgeFor(d.vehicle_doc?.status)}
                    {d.vehicle_doc?.days_left !== null && d.vehicle_doc?.days_left !== undefined && (
                      <span className="text-muted-foreground">({d.vehicle_doc.days_left}d)</span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const full = all.find((x) => x.user_id === d.user_id);
                      setView("all");
                      if (full) startEdit(full);
                      // Scroll target into view after render
                      setTimeout(() => {
                        const el = document.querySelector(`[data-testid="driver-full-${d.user_id}"]`);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 120);
                    }}
                    data-testid={`driver-doc-manage-${d.user_id}`}
                  >
                    Gerenciar docs
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {!loading && view === "all" && (
        <div className="space-y-3">
          {all.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground" data-testid="driver-docs-all-empty">
              Nenhum motorista cadastrado.
            </Card>
          )}
          {all.map((d) => (
            <Card key={d.user_id} className="p-4" data-testid={`driver-full-${d.user_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    {d.name}
                    {d.is_online && <Badge className="bg-emerald-500 text-white text-[10px]">Online</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {d.email} · {d.phone || "sem telefone"} · {[d.vehicle_brand, d.vehicle_model, d.vehicle_plate].filter(Boolean).join(" · ") || "sem veículo"}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span>CNH:</span> {badgeFor(d.cnh_status?.status)}
                  <span>Veículo:</span> {badgeFor(d.vehicle_doc_status?.status)}
                  <Button
                    size="sm"
                    variant={editing?.user_id === d.user_id ? "default" : "outline"}
                    onClick={() => editing?.user_id === d.user_id ? setEditing(null) : startEdit(d)}
                    data-testid={`driver-full-edit-${d.user_id}`}
                  >
                    {editing?.user_id === d.user_id ? "Fechar" : "Gerenciar"}
                  </Button>
                </div>
              </div>

              {/* Thumbnail grid */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {DOC_FIELDS.map((f) => {
                  const url = d.docs?.[f.key];
                  const pdf = isPdfUrl(url);
                  return (
                    <div key={f.key} className="space-y-1">
                      <div className="text-[10px] text-muted-foreground truncate">{f.label}</div>
                      {url ? (
                        pdf ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`driver-full-thumb-pdf-${d.user_id}-${f.key}`}
                            className="w-full h-20 flex items-center justify-center rounded border border-border bg-red-500/10 hover:bg-red-500/20 gap-2 text-[10px] font-semibold text-red-600"
                          >
                            <span className="w-6 h-8 rounded bg-red-500 text-white flex items-center justify-center text-[9px] font-bold">PDF</span>
                            Abrir
                          </a>
                        ) : (
                        <a href={url} target="_blank" rel="noreferrer" data-testid={`driver-full-thumb-${d.user_id}-${f.key}`}>
                          <img
                            src={url}
                            alt={f.label}
                            className="w-full h-20 object-cover rounded border border-border hover:opacity-80"
                            onError={(e) => {
                              // Replace broken image with the 'Sem foto' placeholder
                              const parent = e.currentTarget.parentElement?.parentElement;
                              if (parent && !parent.querySelector("[data-broken='1']")) {
                                const ph = document.createElement("div");
                                ph.className = "w-full h-20 flex items-center justify-center rounded border border-dashed border-border text-[10px] text-muted-foreground";
                                ph.textContent = "Foto inválida";
                                ph.setAttribute("data-testid", `driver-full-broken-${d.user_id}-${f.key}`);
                                ph.setAttribute("data-broken", "1");
                                e.currentTarget.parentElement.replaceWith(ph);
                              }
                            }}
                          />
                        </a>
                        )
                      ) : (
                        <div className="w-full h-20 flex items-center justify-center rounded border border-dashed border-border text-[10px] text-muted-foreground" data-testid={`driver-full-missing-${d.user_id}-${f.key}`}>
                          Sem foto
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Edit panel */}
              {editing?.user_id === d.user_id && (
                <div className="mt-4 pt-4 border-t border-border space-y-3" data-testid={`driver-full-editor-${d.user_id}`}>
                  {DOC_FIELDS.map((f) => (
                    <div key={f.key} className="space-y-1.5">
                      <label className="text-xs font-medium">{f.label}</label>
                      <div className="flex items-center gap-2">
                        <Input
                          data-testid={`driver-full-input-${f.key}`}
                          value={editing.docs[f.key] || ""}
                          onChange={(e) => setEditing({ ...editing, docs: { ...editing.docs, [f.key]: e.target.value } })}
                          placeholder="URL da imagem"
                          className="flex-1"
                        />
                        <ImageUploader
                          testid={`driver-full-upload-${f.key}`}
                          label="Enviar"
                          variant="outline"
                          size="sm"
                          accept={f.accept || "image/*"}
                          onUploaded={(url) => setEditing({ ...editing, docs: { ...editing.docs, [f.key]: url } })}
                        />
                        {editing.docs[f.key] && (
                          <>
                            {DOC_KEY_TO_TYPE[f.key] && !isPdfUrl(editing.docs[f.key]) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => runOcr(editing.user_id, f.key)}
                                disabled={ocrLoading && ocrLoading.user_id === editing.user_id && ocrLoading.key === f.key}
                                data-testid={`driver-full-ocr-${f.key}`}
                                className="gap-1"
                              >
                                {ocrLoading && ocrLoading.key === f.key ? "OCR..." : "OCR"}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => clearDoc(editing.user_id, f.key)}
                              data-testid={`driver-full-clear-${f.key}`}
                            >
                              Excluir
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium">Validade CNH</label>
                      <Input
                        type="date"
                        data-testid="driver-full-cnh-exp"
                        value={editing.docs.cnh_expires_at || ""}
                        onChange={(e) => setEditing({ ...editing, docs: { ...editing.docs, cnh_expires_at: e.target.value } })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium">Validade doc. veículo</label>
                      <Input
                        type="date"
                        data-testid="driver-full-vdoc-exp"
                        value={editing.docs.vehicle_doc_expires_at || ""}
                        onChange={(e) => setEditing({ ...editing, docs: { ...editing.docs, vehicle_doc_expires_at: e.target.value } })}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => setEditing(null)} data-testid="driver-full-cancel">Cancelar</Button>
                    <Button size="sm" onClick={saveDocs} disabled={saving} data-testid="driver-full-save">
                      {saving ? "Salvando..." : "Salvar alterações"}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* OCR result dialog */}
      {ocrResult && (
        <div
          className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setOcrResult(null)}
          data-testid="ocr-result-dialog"
        >
          <Card className="max-w-lg w-full p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold tracking-tight">Dados extraídos por OCR</h3>
              <button onClick={() => setOcrResult(null)} className="text-muted-foreground hover:text-foreground" data-testid="ocr-result-close">✕</button>
            </div>
            <Badge className="mb-3 bg-emerald-500 text-white">
              {ocrResult.doc_type === "cnh" ? "CNH" : ocrResult.doc_type === "identity" ? "Documento de identidade" : "CRLV"}
            </Badge>
            {ocrResult.extracted ? (
              <div className="space-y-1.5" data-testid="ocr-result-fields">
                {Object.entries(ocrResult.extracted).map(([k, v]) => (
                  <div key={k} className="flex items-start gap-3 text-sm border-b border-border pb-1.5">
                    <span className="text-muted-foreground min-w-[140px] capitalize">{k.replaceAll("_", " ")}</span>
                    <span className="font-semibold flex-1 break-words">{v == null || v === "" ? <em className="text-muted-foreground font-normal">não identificado</em> : String(v)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Não foi possível estruturar o resultado. Texto bruto retornado pelo modelo:</p>
                <pre className="text-xs bg-muted p-3 rounded whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="ocr-result-raw">{ocrResult.raw}</pre>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button size="sm" onClick={() => setOcrResult(null)} data-testid="ocr-result-close-btn">Fechar</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
function ModerationTab() {
  const [queue, setQueue] = useState({ ads: [], posts: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ads");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/moderation`);
      setQueue(r.data || { ads: [], posts: [], total: 0 });
    } catch (e) { toast.error("Erro ao carregar fila"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const act = async (kind, id, action) => {
    try {
      await axios.post(`${API}/admin/moderation/${kind}/${id}/${action}`);
      toast.success(action === "approve" ? "Aprovado" : "Rejeitado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="admin-moderation-panel">
      <Card className="p-6 border-amber-500/40 bg-amber-500/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center"><Eye className="w-5 h-5 text-white" /></div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Fila de moderação</h2>
            <p className="text-xs text-muted-foreground">Aprove ou rejeite anúncios e posts com mídia antes que apareçam para os demais usuários.</p>
          </div>
          <Badge className="ml-auto bg-amber-500 text-white" data-testid="moderation-total">{queue.total} pendentes</Badge>
        </div>
      </Card>

      <div className="flex gap-2">
        <Button size="sm" variant={tab === "ads" ? "default" : "outline"} onClick={() => setTab("ads")} className="rounded-full" data-testid="moderation-tab-ads">
          Anúncios ({queue.ads.length})
        </Button>
        <Button size="sm" variant={tab === "posts" ? "default" : "outline"} onClick={() => setTab("posts")} className="rounded-full" data-testid="moderation-tab-posts">
          Posts ({queue.posts.length})
        </Button>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Carregando...</div>}

      {!loading && tab === "ads" && (
        <div className="space-y-3">
          {queue.ads.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground" data-testid="moderation-ads-empty">
              Nenhum anúncio aguardando moderação.
            </Card>
          )}
          {queue.ads.map((a) => (
            <Card key={a.ad_id} className="p-4 flex gap-3" data-testid={`moderation-ad-${a.ad_id}`}>
              {a.image_url && <img src={a.image_url} alt="" className="w-20 h-20 rounded object-cover flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{a.title}</div>
                <div className="text-xs text-muted-foreground">Por {a.author_name}</div>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{a.description}</p>
                {a.target_url && <a href={a.target_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">{a.target_url}</a>}
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <Button size="sm" onClick={() => act("ad", a.ad_id, "approve")} data-testid={`moderation-approve-ad-${a.ad_id}`} className="rounded-full gap-1 bg-emerald-600 hover:bg-emerald-700">
                  <Check className="w-3.5 h-3.5" /> Aprovar
                </Button>
                <Button size="sm" variant="destructive" onClick={() => act("ad", a.ad_id, "reject")} data-testid={`moderation-reject-ad-${a.ad_id}`} className="rounded-full gap-1">
                  <X className="w-3.5 h-3.5" /> Rejeitar
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {!loading && tab === "posts" && (
        <div className="space-y-3">
          {queue.posts.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground" data-testid="moderation-posts-empty">
              Nenhum post com mídia aguardando moderação.
            </Card>
          )}
          {queue.posts.map((p) => (
            <Card key={p.post_id} className="p-4 flex gap-3" data-testid={`moderation-post-${p.post_id}`}>
              {p.image_url && <img src={p.image_url} alt="" className="w-20 h-20 rounded object-cover flex-shrink-0" />}
              {p.video_url && !p.image_url && (
                <video src={p.video_url} className="w-20 h-20 rounded object-cover flex-shrink-0 bg-black" muted playsInline />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground">Por {p.author_name}</div>
                <p className="text-sm mt-1 whitespace-pre-wrap line-clamp-3">{p.content || <em className="text-muted-foreground">(sem texto)</em>}</p>
                {p.video_url && <div className="text-[10px] mt-1 text-primary">🎥 Contém vídeo</div>}
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <Button size="sm" onClick={() => act("post", p.post_id, "approve")} data-testid={`moderation-approve-post-${p.post_id}`} className="rounded-full gap-1 bg-emerald-600 hover:bg-emerald-700">
                  <Check className="w-3.5 h-3.5" /> Aprovar
                </Button>
                <Button size="sm" variant="destructive" onClick={() => act("post", p.post_id, "reject")} data-testid={`moderation-reject-post-${p.post_id}`} className="rounded-full gap-1">
                  <X className="w-3.5 h-3.5" /> Rejeitar
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Vehicle Categories tab (feature 38) ----------
function VehicleCategoriesTab() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { category_id?, name, description, price_multiplier, icon, color, active }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/vehicle-categories`);
      setCats(r.data || []);
    } catch (err) {
      console.error("[Admin] vehicle-cats load", err);
      toast.error("Erro ao carregar categorias");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    name: "", description: "", price_multiplier: 1.0, icon: "🚗", color: "#3b82f6", active: true,
  });
  const startEdit = (c) => setEditing({ ...c });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing?.name?.trim()) { toast.error("Nome obrigatório"); return; }
    setSaving(true);
    try {
      if (editing.category_id) {
        await axios.put(`${API}/admin/vehicle-categories/${editing.category_id}`, editing);
        toast.success("Categoria atualizada!");
      } else {
        await axios.post(`${API}/admin/vehicle-categories`, editing);
        toast.success("Categoria criada!");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir a categoria "${c.name}"? Motoristas cadastrados nela continuarão registrados, mas ela não aparecerá mais para novos cadastros.`)) return;
    try {
      await axios.delete(`${API}/admin/vehicle-categories/${c.category_id}`);
      toast.success("Categoria excluída");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="admin-vehicle-cats-panel">
      <Card className="p-6 border-blue-500/40 bg-blue-500/5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center"><CarFront className="w-5 h-5 text-white" /></div>
          <div className="flex-1">
            <h2 className="text-lg font-bold tracking-tight">Categorias de veículos</h2>
            <p className="text-xs text-muted-foreground">Defina os tipos de veículo disponíveis para motoristas e passageiros (ex.: Básico, Conforto, Black). O multiplicador ajusta o preço final da corrida.</p>
          </div>
          {!editing && (
            <Button data-testid="add-vehicle-cat" onClick={startNew} className="rounded-full gap-1">
              <Plus className="w-4 h-4" /> Nova
            </Button>
          )}
        </div>
      </Card>

      {editing && (
        <Card className="p-5 border-primary" data-testid="vehicle-cat-editor">
          <div className="text-sm font-bold uppercase tracking-wider text-primary mb-3">
            {editing.category_id ? "Editar categoria" : "Nova categoria"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Nome*</label>
              <Input data-testid="cat-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ex.: Conforto" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Descrição</label>
              <Input data-testid="cat-description" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Ex.: Carros com mais espaço" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Multiplicador de preço</label>
              <Input data-testid="cat-price-multiplier" type="number" step="0.05" min="0" value={editing.price_multiplier} onChange={(e) => setEditing({ ...editing, price_multiplier: parseFloat(e.target.value || "1") })} />
              <div className="text-[10px] text-muted-foreground mt-0.5">Ex.: 1.0 = normal, 1.5 = +50%, 2.0 = dobro</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Ícone</label>
                <Input data-testid="cat-icon" value={editing.icon || ""} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} placeholder="🚗" maxLength={4} />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Cor</label>
                <Input data-testid="cat-color" type="color" value={editing.color || "#3b82f6"} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="h-10 p-1" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="cat-active-toggle" />
              <span className="text-sm">Categoria ativa (visível na plataforma)</span>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button data-testid="cat-save" onClick={save} disabled={saving} className="rounded-full">
              {saving ? "Salvando..." : editing.category_id ? "Salvar alterações" : "Criar categoria"}
            </Button>
            <Button variant="outline" onClick={cancel} className="rounded-full">Cancelar</Button>
          </div>
        </Card>
      )}

      {loading && <div className="text-sm text-muted-foreground">Carregando...</div>}
      {!loading && cats.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground" data-testid="vehicle-cats-empty">
          Nenhuma categoria cadastrada. Clique em "Nova" para começar.
        </Card>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {cats.map((c) => (
          <Card key={c.category_id} className="p-4 relative overflow-hidden" data-testid={`vehicle-cat-${c.category_id}`}>
            <div className="absolute top-0 left-0 right-0 h-1" style={{ background: c.color }} aria-hidden />
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="text-2xl">{c.icon || "🚗"}</div>
                <div className="min-w-0">
                  <div className="font-bold truncate">{c.name}</div>
                  <div className="text-[10px] text-muted-foreground">ID: {c.category_id}</div>
                </div>
              </div>
              {!c.active && <Badge variant="outline" className="text-[10px]">Inativa</Badge>}
            </div>
            {c.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{c.description}</p>}
            <div className="text-xs mb-3">
              <span className="text-muted-foreground">Preço:</span>{" "}
              <strong>{Number(c.price_multiplier || 1).toFixed(2)}x</strong>{" "}
              <span className="text-muted-foreground">
                ({c.price_multiplier > 1 ? `+${Math.round((c.price_multiplier - 1) * 100)}%` : c.price_multiplier < 1 ? `${Math.round((c.price_multiplier - 1) * 100)}%` : "normal"})
              </span>
            </div>
            <div className="flex gap-2">
              <Button data-testid={`cat-edit-${c.category_id}`} onClick={() => startEdit(c)} variant="outline" size="sm" className="flex-1 rounded-full">
                Editar
              </Button>
              <Button data-testid={`cat-delete-${c.category_id}`} onClick={() => remove(c)} variant="outline" size="sm" className="rounded-full text-red-500 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}


// ---------- PIX Pending admin tab (feature 39) ----------
function PixPendingTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("marked_paid");
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pix?status=${status}`);
      setItems(r.data || []);
    } catch (err) {
      console.error("[Admin] pix list", err);
      toast.error("Erro ao carregar PIX");
    }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const confirm = async (p) => {
    try {
      await axios.post(`${API}/admin/pix/${p.payment_id}/confirm`, { admin_note: note[p.payment_id] || "" });
      toast.success("PIX confirmado e efeito aplicado!");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao confirmar"); }
  };
  const reject = async (p) => {
    const reason = window.prompt("Motivo da rejeição:", note[p.payment_id] || "");
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/pix/${p.payment_id}/reject`, { admin_note: reason });
      toast.success("PIX rejeitado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao rejeitar"); }
  };

  const statusBadge = (s) => {
    const map = {
      pending: { c: "bg-slate-500", t: "Aguardando pagamento" },
      marked_paid: { c: "bg-blue-500", t: "Marcado como pago" },
      confirmed: { c: "bg-emerald-500", t: "Confirmado" },
      rejected: { c: "bg-red-500", t: "Rejeitado" },
    };
    const b = map[s] || { c: "bg-muted", t: s };
    return <Badge className={`${b.c} text-white text-[10px]`}>{b.t}</Badge>;
  };
  const purposeLabel = (p) => ({
    wallet_deposit: "Depósito carteira",
    investor_deposit: "Aporte investidor",
    ad_boost: "Boost anúncio",
    job_plan: "Plano de trabalho",
    autonomo_plan: "Plano autônomo",
    other: "Outros",
  }[p] || p);

  return (
    <div className="space-y-4 max-w-5xl" data-testid="admin-pix-panel">
      <AdminPaymentMethodsCard />
      <Card className="p-6 border-emerald-500/40 bg-emerald-500/5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0"><QrCode className="w-5 h-5 text-white" /></div>
          <div className="flex-1">
            <h2 className="text-lg font-bold tracking-tight">PIX manuais</h2>
            <p className="text-xs text-muted-foreground">
              Reconcilie pagamentos PIX recebidos na conta da plataforma. Ao confirmar, o efeito é aplicado automaticamente
              (crédito na carteira, ativação de plano, boost de anúncio etc.).
            </p>
            <div className="text-xs mt-2">
              ⚠️ <strong>Antes de confirmar</strong>, verifique no seu extrato bancário se o valor foi recebido de fato.
            </div>
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        {["marked_paid", "pending", "confirmed", "rejected"].map((s) => (
          <Button
            key={s}
            data-testid={`pix-filter-${s}`}
            variant={status === s ? "default" : "outline"}
            size="sm"
            className="rounded-full"
            onClick={() => setStatus(s)}
          >
            {statusBadge(s).props.children} ({items.length && status === s ? items.length : "?"})
          </Button>
        ))}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Carregando...</div>}
      {!loading && items.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground" data-testid="pix-empty">
          Nenhum PIX {status === "marked_paid" ? "aguardando confirmação" : `com status ${status}`}.
        </Card>
      )}

      <div className="space-y-3">
        {items.map((p) => (
          <Card key={p.payment_id} className="p-4" data-testid={`pix-row-${p.payment_id}`}>
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-bold">R$ {Number(p.amount).toFixed(2)}</span>
                  {statusBadge(p.status)}
                  <Badge variant="outline" className="text-[10px]">{purposeLabel(p.purpose)}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {p.user_name} · {p.user_email}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{p.payment_id}</div>
                {p.ref_id && <div className="text-[10px] text-muted-foreground">Ref: {p.ref_id}</div>}
                {p.note && <div className="text-xs italic mt-1">"{p.note}"</div>}
                {p.receipt_url ? (
                  <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-2 text-xs text-primary font-semibold" data-testid={`pix-receipt-${p.payment_id}`}>
                    <img src={p.receipt_url} alt="comprovante" className="w-12 h-12 rounded object-cover border" /> Ver comprovante
                  </a>
                ) : (
                  <div className="text-[11px] text-amber-700 mt-1" data-testid={`pix-no-receipt-${p.payment_id}`}>Sem comprovante anexado</div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">
                  Criado: {new Date(p.created_at).toLocaleString("pt-BR")}
                  {p.marked_paid_at && ` · Pago: ${new Date(p.marked_paid_at).toLocaleString("pt-BR")}`}
                  {p.confirmed_at && ` · Confirmado: ${new Date(p.confirmed_at).toLocaleString("pt-BR")}`}
                </div>
                {p.admin_note && <div className="text-xs mt-1 p-2 rounded bg-muted"><strong>Nota admin:</strong> {p.admin_note}</div>}
              </div>
              {p.status === "marked_paid" && (
                <div className="flex flex-col gap-2 md:min-w-[240px]">
                  <Input
                    data-testid={`pix-note-${p.payment_id}`}
                    placeholder="Observação (opcional)"
                    value={note[p.payment_id] || ""}
                    onChange={(e) => setNote({ ...note, [p.payment_id]: e.target.value })}
                    className="text-xs"
                  />
                  <div className="flex gap-2">
                    <Button data-testid={`pix-confirm-${p.payment_id}`} onClick={() => confirm(p)} size="sm" className="flex-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
                      <Check className="w-3.5 h-3.5 mr-1" /> Confirmar
                    </Button>
                    <Button data-testid={`pix-reject-${p.payment_id}`} onClick={() => reject(p)} size="sm" variant="outline" className="flex-1 rounded-full text-red-500 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Rejeitar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}


// ---------- Ads Manager Review Tab (approve/reject creatives from FUTURO ADS) ----------
function AdsManagerReviewTab() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      // Admin sees all ads via listing endpoint (filtered client-side by review_status)
      const r = await axios.get(`${API}/ads2/admin/ads`);
      setAds(r.data || []);
    } catch (e) {
      // fallback: if the admin listing endpoint doesn't exist, load the user's own ads
      try {
        const r = await axios.get(`${API}/ads2/ads`);
        setAds(r.data || []);
      } catch { setAds([]); }
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const review = async (ad_id, review_status, reason = null) => {
    try {
      await axios.patch(`${API}/ads2/ads/${ad_id}/review`, { review_status, reason });
      toast.success(review_status === "approved" ? "Anúncio aprovado" : "Anúncio rejeitado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao revisar");
    }
  };

  const filtered = ads.filter((a) => filter === "all" || a.review_status === filter);

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Rocket className="w-5 h-5" />
        <h3 className="font-semibold">Revisão de anúncios (FUTURO ADS)</h3>
      </div>
      <div className="space-y-3">
        <div className="flex gap-2">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)}
              data-testid={`ads-review-filter-${f}`}>
              {f === "pending" ? "Pendentes" : f === "approved" ? "Aprovados" : f === "rejected" ? "Rejeitados" : "Todos"}
            </Button>
          ))}
        </div>
        {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Carregando...</p>
          : filtered.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Nenhum anúncio {filter === "all" ? "" : `com status "${filter}"`}.</p>
          : (
          <div className="divide-y">
            {filtered.map((a) => (
              <div key={a.ad_id} className="py-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`ads-review-row-${a.ad_id}`}>
                {a.creative?.media?.[0]?.url && (
                  <img src={a.creative.media[0].url} alt="" className="w-16 h-16 rounded object-cover bg-slate-100"
                    onError={(e) => { e.target.style.display = "none"; }} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{a.name}</span>
                    <Badge variant="outline">{a.format}</Badge>
                    <Badge className={
                      a.review_status === "approved" ? "bg-emerald-500 text-white"
                      : a.review_status === "rejected" ? "bg-rose-500 text-white"
                      : "bg-amber-500 text-white"
                    }>{a.review_status}</Badge>
                  </div>
                  <p className="text-sm font-medium mt-1 truncate">{a.creative?.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.creative?.primary_text}</p>
                  <p className="text-[11px] text-muted-foreground mt-1 font-mono truncate">
                    {a.ad_id} · destino: <a href={a.destination_url} target="_blank" rel="noreferrer" className="underline">{a.destination_url}</a>
                  </p>
                </div>
                <div className="flex gap-2">
                  {a.review_status !== "approved" && (
                    <Button size="sm" onClick={() => review(a.ad_id, "approved")} data-testid={`ads-review-approve-${a.ad_id}`}>
                      Aprovar
                    </Button>
                  )}
                  {a.review_status !== "rejected" && (
                    <Button size="sm" variant="destructive" onClick={() => {
                      const reason = window.prompt("Motivo da rejeição (opcional):");
                      review(a.ad_id, "rejected", reason);
                    }} data-testid={`ads-review-reject-${a.ad_id}`}>
                      Rejeitar
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}


// ---------- Ads Manager — Banner Slots Tab (side rails config) ----------
const SLOT_SIZES = [
  { value: "small",       label: "Pequeno (160×300)",  w: 160, h: 300 },
  { value: "medium",      label: "Médio (200×400)",    w: 200, h: 400 },
  { value: "large",       label: "Grande (240×600)",   w: 240, h: 600 },
  { value: "leaderboard", label: "Card (300×250)",     w: 300, h: 250 },
];
const PAGE_OPTIONS = [
  { value: "*",         label: "Todas as páginas" },
  { value: "/feed",     label: "Feed" },
  { value: "/work",     label: "Trabalho" },
  { value: "/services", label: "Marketplace" },
  { value: "/mobility", label: "Mobilidade" },
  { value: "/chat",     label: "Futuro Chat" },
  { value: "/profile",  label: "Perfil" },
  { value: "/ads-manager", label: "Ads Manager" },
];

function AdsManagerSlotsTab() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const emptyForm = { side: "left", page: "*", size: "medium", position: 0, price_brl: 29.9, label: "", is_active: true, rotate_every_ms: 7000, image_url: "", destination_url: "", link_label: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ads2/admin/banner-slots`);
      setSlots(r.data || []);
    } catch { setSlots([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        position: Number(form.position),
        price_brl: Number(form.price_brl),
        rotate_every_ms: Number(form.rotate_every_ms),
      };
      if (editingId) {
        await axios.patch(`${API}/ads2/admin/banner-slots/${editingId}`, payload);
        toast.success("Slot atualizado");
      } else {
        await axios.post(`${API}/ads2/admin/banner-slots`, payload);
        toast.success("Slot criado");
      }
      setForm(emptyForm); setEditingId(null); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const editSlot = (s) => {
    setEditingId(s.slot_id);
    setForm({
      side: s.side, page: s.page, size: s.size, position: s.position,
      price_brl: s.price_brl, label: s.label || "", is_active: s.is_active,
      rotate_every_ms: s.rotate_every_ms || 7000,
      image_url: s.image_url || "", destination_url: s.destination_url || "", link_label: s.link_label || "",
    });
  };

  const uploadBanner = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const raw = r.data?.url || r.data?.file_url || r.data?.path;
      // Store relative path so banner URL survives moving from preview to production.
      const url = raw?.startsWith("http") ? raw : raw;
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Banner enviado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no upload");
    }
    setUploading(false);
  };

  const clearBanner = async (slot_id) => {
    if (!window.confirm("Remover o banner enviado deste slot? Ele voltará a exibir os anúncios do FUTURO ADS.")) return;
    const slot = slots.find((s) => s.slot_id === slot_id);
    if (!slot) return;
    try {
      await axios.patch(`${API}/ads2/admin/banner-slots/${slot_id}`, {
        side: slot.side, page: slot.page, size: slot.size, position: slot.position,
        price_brl: slot.price_brl, label: slot.label || "", is_active: slot.is_active,
        rotate_every_ms: slot.rotate_every_ms || 7000,
        image_url: "", destination_url: "", link_label: "",
      });
      toast.success("Banner removido"); load();
    } catch { toast.error("Erro ao remover banner"); }
  };

  const remove = async (slot_id) => {
    if (!window.confirm("Excluir este slot? Os anúncios não aparecerão mais nesse local.")) return;
    try {
      await axios.delete(`${API}/ads2/admin/banner-slots/${slot_id}`);
      toast.success("Slot excluído"); load();
    } catch { toast.error("Erro ao excluir"); }
  };

  const BRL = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5" />
        <div>
          <h3 className="font-semibold">Banners laterais (Rails de Anúncios)</h3>
          <p className="text-xs text-muted-foreground">Configure onde os banners "Patrocinado" aparecem, a ordem de rotação, o tamanho e o preço de referência de cada local.</p>
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 p-3 rounded-lg border bg-accent/30">
        <div className="space-y-1">
          <Label>Lado</Label>
          <Select value={form.side} onValueChange={(v) => setForm({ ...form, side: v })}>
            <SelectTrigger data-testid="slot-side"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Esquerdo</SelectItem>
              <SelectItem value="right">Direito</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Página</Label>
          <Select value={form.page} onValueChange={(v) => setForm({ ...form, page: v })}>
            <SelectTrigger data-testid="slot-page"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAGE_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Tamanho</Label>
          <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })}>
            <SelectTrigger data-testid="slot-size"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SLOT_SIZES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Ordem (sequência)</Label>
          <Input type="number" min={0} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="slot-position" />
        </div>
        <div className="space-y-1">
          <Label>Preço de referência (R$)</Label>
          <Input type="number" min={0} step="0.01" value={form.price_brl} onChange={(e) => setForm({ ...form, price_brl: e.target.value })} data-testid="slot-price" />
        </div>
        <div className="space-y-1">
          <Label>Rotação (ms)</Label>
          <Input type="number" min={2000} max={60000} step={500} value={form.rotate_every_ms} onChange={(e) => setForm({ ...form, rotate_every_ms: e.target.value })} data-testid="slot-rotate" />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label>Rótulo (opcional)</Label>
          <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex.: Rail Esq. Home" data-testid="slot-label" />
        </div>
        <div className="flex items-center gap-2 md:col-span-2">
          <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="slot-active" />
          <span className="text-sm">Ativo</span>
        </div>

        {/* Admin uploaded banner */}
        <div className="md:col-span-4 p-3 rounded-lg border bg-background space-y-2" data-testid="slot-banner-upload-block">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="font-semibold text-sm">Banner enviado (opcional)</p>
              <p className="text-xs text-muted-foreground">Se preenchido, este banner aparece <b>antes</b> dos anúncios do FUTURO ADS neste slot.</p>
            </div>
            <label className="inline-flex items-center gap-1 text-sm cursor-pointer px-3 py-2 rounded-md border hover:bg-accent">
              {uploading ? "Enviando..." : "Enviar novo banner"}
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => uploadBanner(e.target.files?.[0])}
                data-testid="slot-banner-upload" disabled={uploading} />
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>URL da imagem</Label>
              <Input value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://... ou faça upload acima"
                data-testid="slot-image-url" />
            </div>
            <div className="space-y-1">
              <Label>Link de destino</Label>
              <Input value={form.destination_url}
                onChange={(e) => setForm({ ...form, destination_url: e.target.value })}
                placeholder="https://futuropdv.com/lp"
                data-testid="slot-destination-url" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Texto do banner (opcional)</Label>
              <Input value={form.link_label}
                onChange={(e) => setForm({ ...form, link_label: e.target.value })}
                placeholder="Ex.: Conheça nossos serviços"
                data-testid="slot-link-label" />
            </div>
          </div>
          {form.image_url && (
            <div className="flex items-center gap-3 pt-2">
              <img src={form.image_url} alt="preview" className="w-16 h-24 rounded object-cover bg-slate-100 border"
                onError={(e) => { e.target.style.display = "none"; }} data-testid="slot-image-preview" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{form.image_url}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, image_url: "" })}>Limpar</Button>
            </div>
          )}
        </div>

        <div className="flex gap-2 md:col-span-2">
          <Button onClick={save} disabled={saving} data-testid="slot-save-btn">
            {saving ? "Salvando..." : (editingId ? "Atualizar slot" : "Criar slot")}
          </Button>
          {editingId && (
            <Button variant="outline" onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancelar</Button>
          )}
        </div>
      </div>

      {/* List */}
      <div>
        <h4 className="font-semibold mb-2">Slots configurados</h4>
        {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Carregando...</p>
          : slots.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Nenhum slot ainda. Crie o primeiro acima.</p>
          : (
            <div className="divide-y">
              {slots.map((s) => (
                <div key={s.slot_id} className="py-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`slot-row-${s.slot_id}`}>
                  {s.image_url && (
                    <img src={s.image_url} alt="banner" className="w-14 h-20 rounded object-cover bg-slate-100 border"
                      onError={(e) => { e.target.style.display = "none"; }}
                      data-testid={`slot-thumb-${s.slot_id}`} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline">{s.side === "left" ? "Esquerdo" : "Direito"}</Badge>
                      <Badge variant="outline">{PAGE_OPTIONS.find((p) => p.value === s.page)?.label || s.page}</Badge>
                      <Badge className="bg-slate-800 text-white">{SLOT_SIZES.find((x) => x.value === s.size)?.label || s.size}</Badge>
                      <Badge className={s.is_active ? "bg-emerald-500 text-white" : "bg-slate-400 text-white"}>
                        {s.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                      {s.image_url && <Badge className="bg-primary text-primary-foreground">Banner enviado</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Ordem #{s.position} · Rotação {(s.rotate_every_ms / 1000)}s · Preço ref.: <b>{BRL(s.price_brl)}</b>
                      {s.label && <> · {s.label}</>}
                    </p>
                    {s.image_url && (
                      <p className="text-[11px] text-muted-foreground truncate">
                        Impressões: {s.admin_impressions || 0} · Cliques: {s.admin_clicks || 0}
                        {s.destination_url && <> · Destino: {s.destination_url}</>}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => editSlot(s)} data-testid={`slot-edit-${s.slot_id}`}>Editar</Button>
                    {s.image_url && (
                      <Button size="sm" variant="outline" onClick={() => clearBanner(s.slot_id)} data-testid={`slot-clear-banner-${s.slot_id}`}>
                        Remover banner
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => remove(s.slot_id)} data-testid={`slot-delete-${s.slot_id}`}>Excluir</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </Card>
  );
}

